// Mirrors backend Pydantic schemas

export interface TeamMemberDisplay {
  role: 'owner' | 'sponsor' | 'team' | 'op' | 'cf';
  name: string;
  display_order: number;
}

/**
 * One bar of the 8-week activity sparkline. `week_start` is an ISO `YYYY-MM-DD` Monday — the shape
 * `ActivityIndicator` already takes, and the shape both boards send: v1 from `deal_weekly_activity`,
 * v2 from `dash_deal_weekly` (backend `ActivityWeekV2`). Named here so the two boards and the sector
 * rollups share ONE declaration instead of four structurally-identical inline ones.
 */
export interface ActivityWeek {
  week_start: string;
  emails: number;
  meetings: number;
}

/**
 * DASH-2v v2-a — one pipeline-board deal read from OUR data, not the Affinity snapshot.
 * Mirrors backend `DealResponseV2`.
 *
 * Deliberately NOT an extension of `Deal`, and the differences are the point rather than an
 * inconvenience:
 *
 * - keyed on `entry_id`; there is **no Affinity id**, because after the cut a natively-created deal
 *   has none. `Deal.affinity_opportunity_id` is a required number and also feeds Affinity deep links
 *   and the Deal Updates cross-link, so reusing that field for an entry id would point links at the
 *   wrong records.
 * - money is **numeric**. The snapshot stores display strings (`"$120M"`); formatting moves to the
 *   view (`formatMoneyMm`), which reproduces v1's format exactly so a side-by-side diff is meaningful.
 * - multi-valued fields are **arrays**. `vertical`, `themes`, `lost_passed_category` and
 *   `financial_advisor` are all multi-valued natively; the snapshot keeps one value (or a joined
 *   string) and silently drops the rest.
 *
 * Activity counts ARE carried now (DASH-2a 2a-4 tier 1) — natively attributed from our own mail and
 * meeting spine — but they are **nullable**, and the null is the contract: `null` means the numbers were
 * not read for this request, so the page keeps its disabled controls; `0` means computed-and-quiet. TWO
 * backend conditions produce that null and the payload does not distinguish them — the
 * `dash_activity_read_enabled` flag off, or a non-admin caller (the board is firm-visible as of
 * 2026-08-10, the pre-parity numbers stay admin-only until the DASH-2a artifact is signed). `activity_weeks` is carried as of DASH-2b — off
 * `dash_deal_weekly`, a genuine per-DEAL series, not a sum of the per-internal-person rollup (which
 * would not add up to these counts in either direction) — and `badges` is native as of v2-b.
 */
export interface DealV2 {
  entry_id: number;
  name: string;
  organization_name: string | null;
  stage: string;
  dashboard_section: string;
  display_order: number;
  enterprise_value: number | null;
  revenue: number | null;
  ebitda: number | null;
  est_investment: number | null;
  sector: string | null;
  vertical: string[];
  themes: string[];
  sourcing_type: string | null;
  sourcing_channel: string | null;
  deal_type: string | null;
  codename: string | null;
  financial_advisor: string[];
  opportunity_description: string | null;
  next_steps: string | null;
  lost_passed_category: string[];
  lost_passed_notes: string | null;
  days_in_stage: number | null;
  /** 'new' | 'moved' | 'moved_back' — v1's predicates over the native history union. */
  badges: string[];
  deal_team: TeamMemberDisplay[];
  deal_lead_name: string | null;
  /** 'manual' = the entry's own Deal Team column; 'none' = no person refs, so no team to rank. */
  deal_team_source: string;
  people: string[];
  /** How many fields carry an open AI proposal (the value-sticky read). >0 renders "Pending Update". */
  pending_count: number;
  /** Can this viewer act on any of them? False → the badge renders disabled, never hidden. */
  pending_clickable: boolean;
  /**
   * Native activity, v1's field names so the two boards share one `ActivityIndicator`.
   * `null` = not read for this request (flag off, or a non-admin caller — see the interface docstring);
   * `0` = computed, and the deal was quiet.
   * Use `hasActivity()` rather than testing a count for truthiness — `0` is data, not absence.
   */
  email_count_7d: number | null;
  email_count_14d: number | null;
  email_count_30d: number | null;
  meeting_count_7d: number | null;
  meeting_count_14d: number | null;
  meeting_count_30d: number | null;
  last_email_at: string | null;
  last_meeting_at: string | null;
  next_meeting_at: string | null;
  /**
   * The 8-week sparkline series, oldest→newest, gaps zero-filled. THREE states, not two:
   * `null`/absent = not read for this request; `[]` = computed and all eight weeks were zero (v1's
   * drop-if-silent — render the collapsed counts); 8 entries = draw the chart.
   */
  activity_weeks?: ActivityWeek[] | null;
}

/**
 * Does this payload carry computed activity? The one predicate the board's three activity affordances
 * (the column, the metric toggle, the 7d-active grouping) share.
 *
 * Keyed on `email_count_7d` because that is the field every one of them needs, and tested against
 * `null` explicitly — `!deal.email_count_7d` would read a genuinely quiet deal as "no data" and
 * disable the controls for the whole board whenever the first deal happened to be silent.
 */
export function hasActivity(deal: DealV2): boolean {
  return deal.email_count_7d !== null && deal.email_count_7d !== undefined;
}

/** Mirrors backend `PipelineBoardV2`. `residue` names what the build could not resolve. */
export interface BoardV2 {
  sections: Record<string, DealV2[]>;
  total_entries: number;
  board_entries: number;
  residue: { entry_id?: number; reason: string; detail?: string }[];
  list_id?: number;
  /** The top tiles, same arithmetic as v1's. Two, not four — see `board_v2._summary_cards`. */
  summary: SummaryCard[];
}

// ⛔ `Deal` — the v1 board's per-deal row — was DELETED 2026-09-02 with the `/api/pipeline` slim.
// It mirrored backend `DealResponse` (also deleted) and carried the money strings, the 7/14/30
// counts, badges, `deal_team`, `activity_weeks` and the Phase B pending pair for a board the
// post-Affinity retirement wave removed. `utils/dealActivity.ts` and `utils/sortDeals.ts` went
// with it; `DealV2` above is the live per-deal shape, and it was never an extension of this one.

export interface SummaryCard {
  label: string;
  value: number;
  sub: string;
  accent: string;
}

// --- Sector types ---

export interface SectorTeamMember {
  name: string;
  email: string;
  title: string | null;
  role: 'owner' | 'sponsor' | 'team' | 'cf' | 'op';
  display_order: number;
}

export interface SectorStatusEntry {
  id: number;
  content: string;
  author_name: string | null;
  created_at: string;
}

export interface Sector {
  id: number;
  name: string;
  description: string | null;
  stage: 'active' | 'monitoring' | 'emerging' | 'on-hold' | 'closed';
  lead_name: string | null;
  lead_email: string | null;
  sponsor_name: string | null;
  sponsor_email: string | null;
  team: SectorTeamMember[];
  latest_status: SectorStatusEntry | null;
  recent_statuses: SectorStatusEntry[];
  status_recency: 'green' | 'orange' | 'red';
  targets: number;
  contacted: number;
  origination: number;
  screening: number;
  live: number;
  activity_weeks: ActivityWeek[];
}

export interface SectorStageChange {
  from_stage: string;
  to_stage: string;
  changed_by_name: string | null;
  changed_at: string;
}

export interface SectorAssignmentChange {
  field_name: 'lead' | 'sponsor';
  from_name: string | null;
  to_name: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

export interface SectorHistory {
  status_entries: SectorStatusEntry[];
  stage_changes: SectorStageChange[];
  assignment_changes: SectorAssignmentChange[];
}

// --- Roster types ---

export interface RosterMember {
  email: string;
  name: string;
  title: string | null;
  team_classification: string | null;
  seniority_rank: number | null;
}

// --- Deal activity types: DELETED 2026-08-27 (Pillar-D funeral) ---
// `ActivityItem` + `DealActivity` were the response shape of `GET /api/deals/{id}/activity`, consumed
// only by the never-enabled `useDealActivity` hook. Both went with the endpoint
// (crm-of-record-plan §5).

// --- Pipeline data ---

/**
 * `GET /api/pipeline` — mirrors backend `PipelineResponse`, and it is TWO fields.
 *
 * The Intel tab is the endpoint's only consumer (`App.tsx`: `SNAPSHOT_TABS` is `{'intel'}`, and it
 * renders `<IntelPage sectors={data.sectors} />`), so `sectors` is the whole payload as far as this
 * app is concerned. The nine deleted fields rendered the v1 board, Metrics, Pipeline by Sector,
 * Agenda and the old Fund II bar, all of which were removed or repointed by the post-Affinity
 * retirement wave.
 *
 * `snapshot_date` is served but not rendered: each sector's deal counts are computed off the frozen
 * `opportunities` snapshot and this says which one — the payload's only freshness disclosure. Read
 * it if Intel's freeze notice comes back; do not add a field here that nothing reads.
 */
export interface PipelineData {
  snapshot_date: string | null;
  sectors: Sector[];
}

// --- Staffing types ---

export interface StaffingDeal {
  // DASH-3 3-1 — the matrix's identity for this deal, and the key `deal_assignments` / `deal_activity`
  // use: the Affinity opportunity id in the frozen era, `crm_list_entry.id` natively.
  deal_key: number;
  affinity_opportunity_id: number | null;
  entry_id: number | null;
  name: string;
  category: 'portfolio' | 'new_deal';
  stage: string | null;
}

export interface StaffingPersonRow {
  email: string;
  name: string;
  short_name: string;
  title: string | null;
  group: 'senior_investment' | 'junior_investment' | 'operating_partners';
  seniority_rank: number;
  deal_assignments: Record<number, string>;
  deal_activity: Record<number, { meetings: number; emails: number }>;
  portfolio_count: number;
  new_deal_count: number;
  total_count: number;
}

export interface StaffingData {
  snapshot_date: string | null;
  snapshot_timestamp: string | null;
  groups: string[];
  group_labels: Record<string, string>;
  people: StaffingPersonRow[];
  portfolio_deals: StaffingDeal[];
  new_deals: StaffingDeal[];
  // DASH-3 3-0 / post-Affinity S4 — where the payload came from. The `affinity_frozen` era and its
  // `frozen_as_of` stamp were deleted with `services/staffing_builder`; what is left is not two eras
  // but "read / could not read". `unavailable` is an EMPTY matrix that must render as a failure to
  // read — never as an idle team, and never as the 2026-08 snapshot the old fallback served.
  source: 'native' | 'unavailable';
  source_change_date: string | null;
  /** The IOT's list id — native payloads only; the record-panel link needs it. */
  list_id: number | null;
}

// --- Analytics types ---

// ⚠ Every candidate-deal field added below is declared OPTIONAL even though the backend always
// sends it (the frozen contract gives each a default —
// `docs/plans/pipeline-core/overview-candidate-deals-spec.md` §3.7). React Query serves a cached
// `/api/analytics/summary` response across a deploy, so the first render after ship can be a payload
// from BEFORE the fields existed. Optional here forces every read site through `?? 0` / `?? []`,
// which is what keeps that render from blanking the page.
export interface AnalyticsSummaryMetric {
  label: string;
  value: number;
  delta: number | null;
  accent: string;
  /** Stable identity for tiles the FE treats specially. `'new_proposals'` is hidden while the
   *  candidate toggle is OFF; every pre-existing tile sends `null`. */
  key?: string | null;
  /** Disclosure rendered as the tile's `title` tooltip (e.g. "incl. 2 since dismissed"). */
  note?: string | null;
}

// One live/origination stage + its weighted-mode probability (0–100).
export interface StageProbability {
  stage_value: string;
  section: string;
  probability_pct: number;
}

// Fund II Overview settings — mirrors backend schemas.FundSettingsResponse.
// All amounts in $mm. Read (user) via /api/fund-settings; written (admin) via
// PATCH /api/admin/fund-settings (scalars) + .../stage-probabilities.
export interface FundSettings {
  committed_capital_mm: number;
  invested_mm: number;
  called_expenses_mm: number;
  reserved_expenses_mm: number;
  per_deal_reserve_cap_mm: number;
  updated_by: string | null;
  updated_at: string | null;
  stage_probabilities: StageProbability[];
}

export interface TrendDataPoint {
  period_label: string;
  snapshot_date: string;
  live: number;
  screening: number;
  origination: number;
  pending: number;
  // Per-stage counts for the granular composition view. Optional so the
  // chart degrades gracefully across a deploy where the backend hasn't
  // shipped the field yet.
  by_stage?: Record<string, number>;
  /** Live proposed (candidate) rows that week — SECTION grain only, never a stage bucket. Weeks
   *  recorded before the producer shipped read 0 on this field alone. */
  proposed?: number;
}

export interface TeamBreakdown {
  person_name: string;
  live: number;
  screening: number;
  origination: number;
  pending: number;
  total: number;
}

/** The single sector row holding every counted deal with no theme (A2). A backend constant; the FE
 *  only needs it to recognise the row in tests. */
export const UNCLASSIFIED_SECTOR = 'Unclassified';

export interface SectorBreakdown {
  sector: string;
  live: number;
  screening: number;
  origination: number;
  pending: number;
  total: number;
  /** Live proposed (candidate) rows carrying that theme. Drawn only with the toggle ON. */
  proposed?: number;
}

export interface VerticalBreakdown {
  vertical: string;
  count: number;
}

export interface HeatmapWeek {
  week_start: string;
  emails: number;
  meetings: number;
}

export interface HeatmapRow {
  // `crm_list_entry.id` — the row's identity and its React key. The two Affinity ids below are
  // vestigial on the native payload (post-Affinity S2) and come back as 0.
  entry_id: number;
  organization_id: number;
  affinity_opportunity_id: number;
  company_name: string;
  group: 'portfolio' | 'pipeline';
  weeks: HeatmapWeek[];
}

/**
 * One deal's inputs to the Fund II deployment bar, carried on the analytics payload since the
 * post-Affinity S2 repoint. Before it, the bar was fed from `/api/pipeline`'s frozen snapshot
 * through props — which is why the Overview tab had to fetch that endpoint at all.
 *
 * `est_investment_mm` is a NUMBER in $mm (the IOT field is "Est. EI Investment ($mm)"), where v1
 * carried a display string that `parseMoneyMM` had to interpret.
 */
export interface FundDeal {
  entry_id: number;
  name: string;
  stage: string;
  section: string;
  deal_type: string | null;
  est_investment_mm: number;
}

/**
 * One person's candidate (D8 proposed) deals, split by how long the candidate has sat unreviewed.
 * "Person" is the row's READ AUDIENCE — whoever can currently see and act on the candidate — so a
 * candidate visible to three people counts once for each (disclosed in the card's caption).
 */
export interface ProposedByPerson {
  person_name: string;
  fresh: number; // ≤ 7 days old
  aging: number; // 8–28 days
  stale: number; // > 28 days
  total: number;
}

/** Firm-wide COUNTS over candidate deals (owner ruling D1 — no names, stages, ids or $ values). */
export interface ProposedSummary {
  count: number;
  fresh: number;
  aging: number;
  stale: number;
  unclassified: number;
  /** Candidates with no audience yet: counted in `count`, attributed to nobody in the chart. */
  no_audience: number;
  new_in_window: number;
  new_in_window_dismissed: number;
}

export interface AnalyticsData {
  snapshot_date: string | null;
  snapshot_timestamp: string | null;
  snapshot_count: number;
  summary_metrics: AnalyticsSummaryMetric[];
  trend_data: TrendDataPoint[];
  trend_period: string;
  by_team: TeamBreakdown[];
  by_sector: SectorBreakdown[];
  by_vertical: VerticalBreakdown[];
  heatmap: HeatmapRow[];
  heatmap_weeks: string[];
  fund_deals: FundDeal[];
  /** DISTINCT committed deals with ≥1 mapped vertical — the donut's centre number. The slice sum is
   *  deal×vertical PAIRS and overstates it (A3), so the centre never uses it. */
  vertical_deal_total?: number;
  /** Counted committed deals whose themes map to no vertical — the donut's footnote (A2). */
  vertical_unclassified?: number;
  /** Sorted `(-total, person_name)` by the backend; the FE renders in payload order. */
  proposed_by_person?: ProposedByPerson[];
  /** `null`/absent only on the empty (fail-closed) payload. */
  proposed_summary?: ProposedSummary | null;
}


// Admin
export interface ImpersonatableUser {
  email: string;
  name: string;
  title: string | null;
  team_classification: string | null;
  is_admin: boolean;
  is_senior_team: boolean;
  is_active: boolean;
}

export interface AdminRosterMember {
  email: string;
  name: string;
  short_name: string | null;
  title: string | null;
  team_classification: string | null;
  seniority_rank: number | null;
  is_active: boolean;
  is_email_approved: boolean;
  is_senior_team: boolean;
  is_origination_scan_enabled: boolean;
  // Outreach campaign engine access — admins always have it; admins can
  // flip this on for non-admin operators who manage campaigns.
  can_use_outreach: boolean;
  is_admin: boolean;
  // CRM + governance access (W1-PR4e-D2-4). can_use_crm gates the CRM tab;
  // crm_ingest_enabled ingests the mailbox; is_governance_admin is the governance
  // edit-right (decoupled from is_admin). is_mp is display-only here (set via the
  // governance MP route, NOT this roster PATCH).
  can_use_crm: boolean;
  crm_ingest_enabled: boolean;
  is_governance_admin: boolean;
  is_mp: boolean;
}

export interface AdminRosterUpdate {
  name?: string;
  short_name?: string;
  title?: string;
  team_classification?: string;
  seniority_rank?: number;
  is_active?: boolean;
  is_email_approved?: boolean;
  is_senior_team?: boolean;
  is_origination_scan_enabled?: boolean;
  can_use_outreach?: boolean;
  // W1-PR4e-D2-4 — settable via the require_admin roster PATCH. NOT is_mp (that is
  // governance-only, set via PUT /api/crm/admin/managing-partner).
  can_use_crm?: boolean;
  crm_ingest_enabled?: boolean;
  is_governance_admin?: boolean;
}

// --- Schedules (in-app cron) ---

// A scheduler action KEY. Deliberately `string`, not a closed union: the set of
// runnable actions is owned by the backend `ACTION_HANDLERS` registry and served
// by `GET /api/admin/schedule-actions` (see `useScheduleActions`). The editor
// builds its picker from that catalog, so a newly-registered backend action
// auto-appears with no frontend edit — no more hand-maintained lists to drift
// out of lockstep (the #535 failure mode).
export type ScheduleAction = string;
// `skipped` = the fire ran and the handler reported it did no work (a disabled flag, an
// unconfigured dependency). Distinct from `skipped_late`, which means the fire never happened
// because it was too far past its tick. Both are non-failures; neither is `success`.
export type ScheduleStatus = 'success' | 'failed' | 'skipped' | 'skipped_late' | null;

// One selectable action from the backend catalog. `danger` is an advisory flag
// (live provider calls / spend / destructive) — the picker shows a badge + a
// confirm on add; it is NOT a permission.
export interface ScheduleActionCatalogItem {
  key: string;
  label: string;
  description: string;
  category: string;
  danger: boolean;
}

export interface Schedule {
  id: number;
  name: string;
  cron_expr: string;
  timezone: string;
  actions: ScheduleAction[];
  is_active: boolean;
  last_fired_at: string | null;
  last_status: ScheduleStatus;
  last_error: string | null;
  max_lateness_hours: number;
  next_fire_at: string | null;  // null when cron_expr is invalid
}

export interface ScheduleCreate {
  name: string;
  cron_expr: string;
  timezone?: string;
  actions: ScheduleAction[];
  is_active?: boolean;
  max_lateness_hours?: number;
}

export interface ScheduleUpdate {
  cron_expr?: string;
  timezone?: string;
  actions?: ScheduleAction[];
  is_active?: boolean;
  max_lateness_hours?: number;
}

// One audit row per scheduler action invocation. Powers the Admin
// "Recent runs" panel.
export type ScheduleRunStatus =
  'running' | 'success' | 'failed' | 'skipped' | 'skipped_late';

export interface ScheduleRunRow {
  id: number;
  schedule_id: number;
  schedule_name: string;
  action_name: string;
  started_at: string;
  finished_at: string | null;
  status: ScheduleRunStatus;
  error: string | null;
  result_summary_json: string | null;
  duration_seconds: number | null;
}

export interface ScheduleRunsResponse {
  rows: ScheduleRunRow[];
  running_count: number;
}

// --- Audit log (generic — used by suggestions + intel) ---

export type AuditActorVia = 'user' | 'user_triggered' | 'system';

export interface AuditEntry {
  id: number;
  entity_id: number;
  action_type: string;
  actor_via: AuditActorVia | string;
  actor_email: string | null;
  actor_email_actual: string | null;
  prior_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  details: Record<string, unknown> | null;
  fields_changed: string[];
  created_at: string;
}

export interface AuditLogResponse {
  entity_type: 'suggestion' | 'intel';
  entity_id: number;
  entries: AuditEntry[];
  total: number;
}

export type AuditEntityType = 'suggestion' | 'intel';

// --- Tab Visibility ---

export type TabRole = 'everyone' | 'senior' | 'admin';

export interface TabVisibilityRow {
  tab_key: string;
  label: string;
  min_role: TabRole;
  display_order: number;
  globally_hidden: boolean;
}

export interface MyTabsResponse {
  visible: string[];
  role: TabRole;
  // Outreach campaign engine access. True for admins or any roster row
  // with team_roster.can_use_outreach=1. Controls Outreach tab
  // visibility + endpoint access.
  can_use_outreach: boolean;
  // W0-PR1: capabilities resolved from the ACTUAL identity (so admin controls
  // survive "view as"), unlike `role`/`can_use_outreach` which are effective.
  // FE admin controls should read capabilities.is_admin, not a hardcoded list.
  capabilities?: {
    is_admin: boolean;
    is_senior: boolean;
    can_use_outreach: boolean;
    can_use_intel: boolean;
    // CRM governance capabilities (W1-PR4e-D2), resolved from the ACTUAL identity
    // so they survive "view as". Decoupled from is_admin. Gate the Admin-tab
    // Governance group + MP affordances on these.
    is_governance_admin: boolean;
    is_mp: boolean;
    impersonating: boolean;
  };
}

// --- CRM Governance (W1-PR4e-D2, Admin-tab Governance group) ---
// Mirrors backend/app/crm/governance_admin.py. NOTE: is_system is an INT (0|1),
// not a JSON boolean — the backend serializes it as a number.

export interface CrmViewerClass {
  id: number;
  name: string;
  is_system: 0 | 1;
  member_count: number;
}

export interface CrmViewerClassMember {
  person_id: number;
  name: string;
  email: string;
}

// crm_access_audit row (GET /api/admin/crm-audit). `at` is an ISO timestamp;
// principal/override ids are crm_person ids (null for service/unresolved).
export interface CrmAuditEntry {
  id: number;
  principal_id: number | null;
  action: string;
  object_kind: string | null;
  object_id: number | null;
  outcome: string;
  override_principal_id: number | null;
  view_as: string | null;
  reason: string | null;
  detail: string | null;
  at: string | null;
}

export interface CrmAuditResponse {
  rows: CrmAuditEntry[];
  count: number;
}

// ── §6.2 Protections (ceilings) — W1-PR4e-D2-2 ───────────────────────────────
// A "Protection" caps a Group's view of one protected person's content at a Level.
export type CrmLevel = 'L1' | 'L2' | 'L3';

export interface CrmProtectedPerson {
  person_id: number;
  name: string;
  email: string;
  policy_count: number;
}

export interface CrmCeilingRow {
  class_id: number;
  name: string;
  ceiling: CrmLevel;
}

export interface CrmCeilings {
  person: { person_id: number; name: string; email: string };
  rows: CrmCeilingRow[];
}

// A proposed policy the editor sends to PUT/impact.
export interface CrmCeilingPolicy {
  class_id: number;
  ceiling: CrmLevel;
}

// /impact preview: a HARD direction guarantee + a LABELED upper-bound estimate
// (never an exact per-viewer count) + non-blocking floor-coverage warnings.
export interface CrmCeilingImpact {
  direction: 'widen' | 'narrow';
  safe: boolean;
  items_affected_estimate: number;
  viewers_affected_estimate: number;
  estimated: boolean;
  warnings: string[];
}

export interface CrmSetCeilingsResult {
  person_id: number;
  widened: boolean;
  warnings: string[];
  policies: CrmCeilingPolicy[];
}

// ── §6.3 Clearances (axis-1 class grants) — W1-PR4e-D2-2 ─────────────────────
// firm_wide is the auto/locked directory baseline (never a toggle). The three
// sensitive tiers are grantable (a grant is a widen; a revoke a synchronous narrow).
export type CrmGrantableClass = 'team_deal' | 'team_ir' | 'mnpi_restricted';

export interface CrmClassGrants {
  person_id: number;
  email: string;
  firm_wide: { auto: boolean };
  team_deal: boolean;
  team_ir: boolean;
  mnpi_restricted: boolean;
}

export interface CrmClassGrantImpact {
  direction: 'widen';
  safe: boolean;
  items_visible_estimate: number;
  estimated: boolean;
}

// ── §6.4 Default visibility (bucket defaults) + Managing Partner — W1-PR4e-D2-3 ─
// The 4 firm-wide starting levels per content-kind × scope (all L2 today).
export type CrmBucket = 'external_email' | 'internal_email' | 'external_meeting' | 'internal_meeting';

export interface CrmBucketDefault {
  bucket: CrmBucket;
  level: CrmLevel;
}

export interface CrmBucketDefaultImpact {
  bucket: CrmBucket;
  direction: 'widen' | 'narrow';
  safe: boolean;
  items_affected_estimate: number;
  estimated: boolean;
}

// ── SG-3 Sharing groups (per-Group, per-direction email grants) ──────────────
// A sharing-group grant is a RAISE only: L2 (Metadata) / L3 (Full), keyed on email direction
// (internal/external), set independently per direction (D7). '' in the UI = Off (no grant).
export type CrmEmailScope = 'internal' | 'external';
export type CrmGrantLevel = 'L2' | 'L3';
// SG-3c: a grant is per content_kind (email / meeting=calendar). The FE couples them by default.
export type CrmContentKind = 'email' | 'meeting';

export interface CrmGroupGrant {
  class_id: number;
  class_name: string;
  content_kind: CrmContentKind;
  email_scope: CrmEmailScope;
  level: CrmGrantLevel;
  reason: string | null;
}

// /impact preview: a grant is ALWAYS a widen; a LABELED per-(content_kind, direction) upper-bound estimate.
export interface CrmGroupGrantImpact {
  direction: 'widen';
  safe: boolean;
  content_kind: CrmContentKind;
  email_scope: CrmEmailScope;
  level: CrmGrantLevel;
  items_visible_estimate: number;
  estimated: boolean;
}

export interface CrmSetGroupGrantResult {
  class_id: number;
  class_name: string;
  content_kind: CrmContentKind;
  email_scope: CrmEmailScope;
  level: CrmGrantLevel;
  old_level: CrmGrantLevel | null;
  widened: boolean;
  changed: boolean;
}

export interface CrmSetManagingPartnerResult {
  email: string;
  is_mp: boolean;
  changed: boolean;
}

// ── R1c full deal access (managing-partner tier) ──────────────────────────────
// The compliance-approved capability to see ALL IOT deal rows + ALL evidence bodies + ALL provenance
// and ACT on all rows. An explicit per-person grant — ADMIN_EMAILS holders hold it via the resolver
// floor instead and are deliberately NOT in these lists.

export interface CrmFullDealAccessGrant {
  person_id: number;
  email: string;
  name: string;
  granted_by: string | null;
  /** ISO timestamp of the grant, off the capability row itself. */
  granted_at: string | null;
  /** False = the holder is no longer an active roster member (a revoke candidate). */
  roster_active: boolean;
}

export interface CrmFullDealAccessCandidate {
  person_id: number;
  email: string;
  name: string;
  title: string | null;
}

export interface CrmFullDealAccessResponse {
  grants: CrmFullDealAccessGrant[];
  candidates: CrmFullDealAccessCandidate[];
}

export interface CrmFullDealAccessMutationResult {
  person_id: number;
  email: string;
  granted?: boolean;
  revoked?: boolean;
  reason: string;
}

// --- Sector Coverage types ---

export type SectorCoverageKind = 'active' | 'non_active';

export interface SectorCoverageSector {
  id: number;
  name: string;
  description: string | null;
  stage: string;
  kind: SectorCoverageKind;
  sponsor_email: string | null;
  sponsor_name: string | null;
}

export interface SectorCoverageCycle {
  id: number;
  label: string;
  opened_at: string;
  opened_by: string | null;
  closed_at: string | null;
  notes: string | null;
}

export interface SectorCoverageBootstrap {
  cycle: SectorCoverageCycle | null;
  sectors: SectorCoverageSector[];
  is_eligible: boolean;
  is_senior: boolean;
  is_admin: boolean;
}

export interface SectorCoveragePick {
  sector_id: number;
  rank: number;
}

export interface SectorCoverageMyPreferences {
  cycle_id: number | null;
  picks: SectorCoveragePick[];
  write_in: string | null;
  submitted_at: string | null;
}

export interface SectorCoveragePreferencesRequest {
  picks: SectorCoveragePick[];
  write_in?: string | null;
}

export interface SectorCoverageUserPick {
  sector_id: number;
  sector_name: string;
  rank: number;
}

export interface SectorCoverageUserSubmission {
  user_email: string;
  user_name: string | null;
  title: string | null;
  seniority_rank: number | null;
  submitted_at: string | null;
  picks: SectorCoverageUserPick[];
  write_in: string | null;
}

export interface SectorCoverageMatrix {
  cycle: SectorCoverageCycle | null;
  eligible_users: string[];
  submissions: SectorCoverageUserSubmission[];
  sectors: SectorCoverageSector[];
}

export interface SectorCoverageAssignmentSector {
  sector_id: number;
  name: string;
  stage: string;
  kind: SectorCoverageKind;
  rank_chosen: number | null;
  points: number;
}

export interface SectorCoverageAssignment {
  user_email: string;
  user_name: string | null;
  sectors: SectorCoverageAssignmentSector[];
  total_points: number;
}

export interface SectorCoverageWriteIn {
  user_email: string;
  user_name: string | null;
  suggestion: string;
}

export interface SectorCoverageOptimizeResponse {
  cycle_id: number;
  run_at: string;
  status: 'optimal' | 'feasible' | 'relaxed' | 'no_solution';
  total_score: number;
  max_possible_score: number;
  assignments: SectorCoverageAssignment[];
  sectors_uncovered: SectorCoverageAssignmentSector[];
  write_ins: SectorCoverageWriteIn[];
  warnings: string[];
  config: { max_per_user?: number | null; submitter_count?: number };
}

export interface SectorCoverageOpenCycleRequest {
  label: string;
  notes?: string | null;
}

export interface NotifyResult {
  emails_sent: number;
  deals_flagged: number;
  errors: string[];
}

// Deal-resolve shadow-logging payload. Mirrors
// `DealResolution.to_payload()` in backend/app/services/deal_resolver.py.
// Surfaces on every scanned email; null when the resolver did not run.
export interface DealResolveOutcome {
  deal_kind: 'affinity_opp' | 'candidate' | 'none';
  deal_key: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  method: string;
  score: number;
  portco_signal: 'operations' | 'exit' | 'add_on' | 'none';
  matched_opp_id: number | null;
  matched_org_id: number | null;
  matched_stage: string | null;
  matched_portco_name: string | null;
  reasons: string[];
}

export interface SignalTriageRow {
  signal_id: number;
  user_email: string;
  source_type: string;
  sender: string | null;
  subject: string | null;
  received_at: string | null;
  created_at: string;
  status: string;
  discard_reason: string | null;
  pre_filter_score: number | null;
  pre_filter_reasons: string[];
  is_origination_signal: boolean | null;
  signal_type: string | null;
  confidence: string | null;
  classifier_reason: string | null;
  company_name: string | null;
  project_codename: string | null;
  // PR-1 — deal-resolve shadow-logging surface
  deal_resolve_method: string | null;
  deal_resolve_outcome: DealResolveOutcome | null;
  suggestion_id: number | null;
  suggestion_status: string | null;
}

export interface SignalTriageResponse {
  rows: SignalTriageRow[];
  total: number;
  counts_by_status: Record<string, number>;
  counts_by_discard_reason: Record<string, number>;
}

// --- Deal-resolve telemetry (Engine tab) ---

export interface DealResolveSampleRow {
  signal_id: number;
  user_email: string;
  sender: string | null;
  subject: string | null;
  received_at: string | null;
  method: string;
  confidence: string;
  score: number;
  portco_signal: string;
  matched_opp_id: number | null;
  matched_portco_name: string | null;
  reasons: string[];
}

export interface DealResolveSummaryResponse {
  total_signals_with_outcome: number;
  counts_by_method: Record<string, number>;
  /** Full method strings split by ':' suffix — 'affinity_match:codename' etc. */
  counts_by_submethod: Record<string, number>;
  /** Of affinity_match rows, which extraction channel found the candidate.
   *  Keys: 'codename' | 'parenthetical' | 'sender_domain' | 'classifier_supplied' | 'unknown'. */
  counts_by_extraction_source: Record<string, number>;
  counts_by_portco_signal: Record<string, number>;
  counts_by_confidence: Record<string, number>;
  bound_count: number;
  unbound_count: number;
  hard_filter_cohort_count: number;
  sample: DealResolveSampleRow[];
  deal_event_log_total: number;
}

export interface MissedBindingRow {
  signal_id: number;
  user_email: string;
  sender: string | null;
  subject: string | null;
  received_at: string | null;
  /** Heuristic tags — 'subject_has_project_codename' | 'subject_has_parenthetical' | 'external_sender_domain' */
  miss_reasons: string[];
}

export interface MissedBindingsResponse {
  rows: MissedBindingRow[];
  total: number;
  total_no_match: number;
}

// --- Deal aliases ----------------------------------------------------------

export type DealAliasMatchMode = 'project_phrase' | 'bare_token';

export interface DealAliasRow {
  id: number;
  alias_token: string;
  /**
   * ⭐ The target as a DealRef PAIR (WP-4 stage 3). `target_kind` is the identity space
   * ('affinity_opp' for every row that exists today; native 'list_entry' targets arrive with
   * stage 6) and `target_key` is the id within it.
   */
  target_kind: string;
  target_key: string;
  /**
   * The LEGACY Affinity opp id — null on a native target, which has no opp. Unchanged for every
   * affinity row, which is what keeps this response compatible for the consumers that still
   * speak Affinity ints.
   */
  target_opp_id: number | null;
  target_opp_name: string | null;
  /** 'user_override' | 'auto_codename' | 'auto_parenthetical' | 'operator_merge' | 'nda_rebind' */
  source: string;
  /**
   * How the token is allowed to match a SUBJECT (owner ruling 2026-09-02):
   * 'project_phrase' — only in the `Project <token>` form (every codename-derived alias);
   * 'bare_token'     — anywhere in the subject (human-declared company shorthands, e.g. `mes`).
   * Editable here so widening ONE alias is an audited operator act, not a matcher change.
   */
  match_mode: DealAliasMatchMode;
  created_by: string;
  created_at: string;
  last_matched_at: string | null;
  match_count: number;
  is_active: boolean;
  disabled_by: string | null;
  disabled_at: string | null;
  disabled_reason: string | null;
}

export interface DealAliasListResponse {
  rows: DealAliasRow[];
  total: number;
}

/**
 * Body for POST /api/admin/deal-aliases — the operator's hand-seeded alias, and the only writer of
 * `user_override` rows left standing (the legacy Suggestions rebind UI is retired).
 * `match_mode` defaults to `bare_token`, which is what a human-declared shorthand gets anyway.
 */
export interface DealAliasCreateRequest {
  alias_token: string;
  target_opp_id: number;
  match_mode: DealAliasMatchMode;
}

/**
 * The 409 body a refused hand-seed comes back with. The reason is the gate that said no
 * (`stoplist` | `conflict` | `already_active` | `already_present` | `disabled` | …); `message` is
 * the sentence the form shows the operator, because "conflict" alone is unactionable.
 */
export interface DealAliasRefusal {
  result: string;
  reason: string;
  message: string;
}

// ⛔ The Metrics tab's team-activity types (`ClassificationBucket`, `ClassificationBreakdown`,
// `TeamActivityMonth` / `Totals` / `FiltersApplied` / `Response` / `Filters`) were deleted on
// 2026-09-01 with the tab, the `useTeamActivity` hook, `GET /api/analytics/team-activity` and
// `team_activity_builder` (post-Affinity S1). Nothing else read them.


// ============================================================================
// Phase B — Deals tab
// ============================================================================
// Mirrors backend deal_card_builder dataclasses (services/deal_card_builder.py).

export type DealKind = 'candidate' | 'affinity_opp';
export type DealBadge = 'NEW_DEAL' | 'FIELD_UPDATE';
export type DealStatusChip = 'pending' | 'pushed' | 'dismissed' | 'all';
export type DealMineTeam = 'mine' | 'team' | 'all';

export interface VisibleToEntry {
  email: string;
  event_count: number;
  // null for team-derived entries (DEAL_TEAM_AUDIENCE_ENABLED) — no evidence
  // basis, so no confidence and event_count is 0.
  top_confidence: 'high' | 'medium' | null;
  // Option B (2026-05-22) — how the user got onto visible_to:
  //   'inbox'     — their mailbox surfaced this message (scanner attribution)
  //   'sender'    — they sent the message (internal sender enrichment)
  //   'recipient' — they were on To/CC (internal recipient enrichment)
  //   'official_team'  — on the Affinity deal team (deal-team audience flag)
  //   'proposed_team'  — on the proposed candidate deal team (deal-team audience flag)
  sources: string[];
}

export interface DealListItem {
  deal_key: string;
  deal_kind: DealKind;
  badge: DealBadge;
  display_name: string;
  pending_count: number;
  deal_lead: string | null;
  last_event_at: string | null;
  stage_abbrev: string | null;
  is_add_on: boolean;
  status: string;
  // Slice J §1 — distinct users whose inboxes contributed bound evidence
  // (confidence ≥ medium). Viewer pinned at index 0 when present; capped at 8.
  visible_to: string[];
  // Wave C #13 — days since last event. null when no events yet.
  days_since_last_event: number | null;
  // Wave C #14 — operator refute count in the last 7 days. Render
  // ⚠ frequent-unbinds badge when ≥ 3.
  refute_count_7d: number;
  // True while the deal is queued/in-flight for re-aggregation (e.g. just
  // after a merge). Drives the "aggregating…" indicator + list polling.
  needs_aggregation: boolean;
  // PR B — write-eligible fields not yet pushed since last log (>= pending_count).
  // When it exceeds pending_count the row has reviewed-but-unpushed work and the
  // list shows a "needs push" marker (so the deal doesn't look done).
  unpushed_field_count: number;
}

export interface DealsListResponse {
  deals: DealListItem[];
  viewer_email: string;
  viewer_is_admin: boolean;
  filters: {
    chip: DealStatusChip;
    mine_team: DealMineTeam;
    search: string | null;
    user_filter: string[];
  };
  // Aggregator telemetry (still sent by the backend; the FE footer that
  // rendered it was removed 2026-07).
  footer: {
    pending_total: number;
    visible_count: number;
    last_aggregated_at: string | null;
  };
}

export interface FieldProposal {
  field_name: string;
  proposed_value: unknown;
  prior_value: unknown;
  decision: string | null;
  confidence: string | null;
  reason: string | null;
  evidence_event_ids: number[];
  user_set: boolean;
  materiality: string | null;
}

export type FieldStatus =
  | 'unchanged'
  | 'proposed'
  | 'edited'
  | 'accepted'
  | 'dismissed'
  | 'push_failed';

/**
 * Resolved per-field state (field-UX-refinements spec §10.1). The backend
 * resolver is the single source of truth; the FE renders from `status` rather
 * than re-inferring from prior/proposed/decision. A dismissed field stays in
 * this list (unlike legacy `fields`, which excludes it) so its row remains
 * visible with the dismissed treatment + Restore affordance.
 */
export interface ResolvedFieldState {
  field_name: string;
  field_kind: string;
  status: FieldStatus;
  effective_value: unknown;
  prior_value: unknown;
  proposed_value: unknown;
  dismissed_proposed_value: unknown;
  write_value: unknown;
  write_eligible: boolean;
  write_eligible_log_deal: boolean;
  write_eligible_push_updates: boolean;
  write_ineligible_reason: string | null;
  create_only: boolean;
  pending: boolean;
  source: string;
  decision: string | null;
  confidence: string | null;
  materiality: string | null;
  reason: string | null;
  evidence_event_ids: number[];
  suggestion_id: number | null;
  aggregator_run_id: number | null;
  dismiss_event_id: number | null;
  restore_event_id: number | null;
  dismiss_reason: string | null;
  dismiss_note: string | null;
  dismissed_at: string | null;
  dismissed_by: string | null;
  push_audit_id: number | null;
  push_error: string | null;
  has_active_ai_delta: boolean;
  has_user_delta: boolean;
  ai_added_elements: unknown[] | null;
  ai_removed_elements: unknown[] | null;
  user_added_elements: unknown[] | null;
  user_removed_elements: unknown[] | null;
  // Edit history for the "· Edited" hover (newest-first); empty when none.
  edit_history?: EditHistoryEntry[];
}

export interface EvidenceItem {
  event_log_id: number;
  occurred_at: string | null;
  sender: string | null;
  subject: string | null;
  attributed_user: string | null;
  source_type: string | null;
  resolver_method: string | null;
  classifier_signal_type: string | null;
  classifier_confidence: string | null;
  // Slice J §J5 — Subject / From / To / Time grid + expand for the body.
  recipients_to: string[];
  // Pillar F.5 (2026-08-20) — the row carries NO body text. `body_available` is the server's
  // "there is a body here AND you may read it" (admin OR inbox-owner — the same gate that used
  // to populate the removed `body_preview`); the text is fetched per row, on click, from
  // `…/evidence/{event_log_id}/body`. Never put body content back on this type.
  body_available: boolean;
  // 2026-05-25 dedup — count of distinct Ember mailbox scans collapsed
  // into this row. 1 = no dedup; >1 renders as a "+N inboxes" badge so
  // the user knows the email was seen by multiple teammates.
  inbox_count: number;
  // E1b-0 — "may YOU detach THIS row from this deal": the server's own `may_refute_event` for this
  // caller and this row (cross-team OR in the deal's audience OR your own mailbox). The ⚑ renders
  // from this, never from the row merely being visible — a visible row is not a refutable one, most
  // visibly on a meeting, which has no inbox owner and is shown to everyone.
  can_refute: boolean;
}

export interface ParticipantItem {
  id: number;
  entity_kind: string;
  affinity_entity_id: number | null;
  extracted_name: string | null;
  extracted_email: string | null;
}

export interface EditHistoryEntry {
  edited_by: string;
  edited_at: string;
}

export interface RecentUpdates {
  progress_html: string;
  new_info_html: string;
  progress_source: 'user' | 'ai';
  new_info_source: 'user' | 'ai';
  // Dismissed (suppressed → omitted from the note); operator can restore.
  progress_suppressed: boolean;
  new_info_suppressed: boolean;
  // Edit history for the "edited" hover (newest-first).
  progress_history?: EditHistoryEntry[];
  new_info_history?: EditHistoryEntry[];
}

export interface DealDetailPayload {
  deal_key: string;
  deal_kind: DealKind;
  badge: DealBadge;
  display_name: string;
  company_name: string | null;
  codename: string | null;
  stage: string | null;
  proposed_stage: string | null;
  deal_team_emails: string[];
  deal_lead: string | null;
  proposed_changes_since_last_log: string[];
  health_flags: string[];
  fields: FieldProposal[];
  // field-UX-refinements spec §10.2. Present on current backends; the FE
  // falls back to `fields` when absent (stale bundle / older backend).
  field_states?: ResolvedFieldState[];
  field_states_schema_version?: string;
  participants: ParticipantItem[];
  evidence_visible: EvidenceItem[];
  evidence_hidden_count: number;
  custom_instructions: string | null;
  proposed_note_title: string | null;
  proposed_note_body_html: string | null;
  user_edited_note_text: string | null;
  user_edited_note_at: string | null;
  // Recent Updates two-block editor (Progress + New Info). null when no
  // current suggestion. *_html doubles as the editor seed; *_source is
  // 'user' (operator override) or 'ai' (synthesized bullets).
  recent_updates: RecentUpdates | null;
  last_aggregated_at: string | null;
  aggregator_model: string | null;
  aggregator_cost_usd: number | null;
  last_event_at: string | null;
  status: string;
  last_logged_at: string | null;
  affinity_opp_id: number | null;
  parent_affinity_opp_id: number | null;
  is_add_on: boolean;
  pending_count: number;
  is_pending: boolean;
  // PR B — write-eligible fields not yet pushed since the last log (pending +
  // accepted/edited newer than last_logged_at). Gates the Push/Log affordance
  // so reviewing every field doesn't hide the button. has_unpushed_note_edit
  // covers note-only edits.
  unpushed_field_count: number;
  has_unpushed_note_edit: boolean;
  // True while queued/in-flight for re-aggregation (e.g. just after a merge);
  // drives the "aggregating…" indicator on the card until it clears.
  needs_aggregation: boolean;
  // Card-level activity record (footer): most-recent field edit + Affinity push.
  last_edited_at: string | null;
  last_edited_by: string | null;
  last_pushed_at: string | null;
  last_pushed_by: string | null;
  // Slice J §1 — attribution-based visibility (full set, not capped here).
  visible_to: string[];
  visible_to_meta: VisibleToEntry[];
  // Queue-only Phase B (owner ruling 2026-08-20) — the durable, cross-process
  // replacement for the old in-memory live-progress view. Non-null while a
  // `phase-b-aggregate-deal` / `phase-b-deal-rerun` job for THIS deal is
  // queued or running; null once it lands (or if nothing is pending — the
  // daily cron can still owe an aggregation with no job queued yet, which is
  // what `needs_aggregation` alone continues to cover). Detail payload only —
  // the list item does not carry this.
  phase_b_job: PhaseBJobState | null;
}

export interface PhaseBJobState {
  job_id: number;
  job_type: 'phase-b-aggregate-deal' | 'phase-b-deal-rerun';
  status: 'queued' | 'running';
  queued_at: string | null;
  started_at: string | null;
}

export interface ProposalDriftConflict {
  field_name: string;
  snapshot_value: unknown;
  live_value: unknown;
  proposed_value: unknown;
}

// `ProposalDrift` (Wave A #24a's `proposal_drift` key on the deal detail payload) was deleted with
// `services/deal_live_compare.py` and the yellow drift banner by the Pillar-D funeral, 2026-08-27.
// `ProposalDriftConflict` above SURVIVES: it also types `isDriftDetail`'s HTTP-409 payload shape in
// `DealsPage.shared.ts`.

export type DealDismissReason =
  // Slice J §J4 — new-deal-specific
  | 'portco_operations'
  | 'portco_exit'
  | 'duplicate'
  | 'newsletter_missed'
  // Slice J §J4 — field-update-specific
  | 'wrong_match'
  | 'not_an_update'
  | 'poor_update_quality'
  | 'not_actionable'
  // Shared
  | 'pass_terminal'
  | 'other'
  // Pre-Slice-J taxonomy (still accepted for backwards-compat)
  | 'not_a_deal'
  | 'wrong_company_or_codename'
  | 'irrelevant_to_strategy'
  | 'already_tracked_elsewhere';

export type FieldDismissReason =
  | 'wrong_value'
  | 'not_material'
  | 'out_of_scope'
  | 'keep_my_value'
  | 'other';

// ── Phase B-3b Learning Portal ─────────────────────────────────────────────

export interface LearningOverviewResponse {
  window: { since_days: number; cutoff: string };
  counts: {
    deal_dismissals: number;
    field_dismissals: number;
    field_edits: number;
  };
  top_dismiss_reasons: Array<{ granularity: string; reason: string; count: number }>;
  top_edited_fields: Array<{ field: string; count: number }>;
}

export interface LearningDismissalRow {
  id: number;
  deal_kind: string;
  deal_key: string;
  granularity: 'deal' | 'field';
  field_name: string | null;
  prior_value: unknown;
  proposed_value: unknown;
  proposed_decision: string | null;
  proposed_confidence: string | null;
  proposed_reason: string | null;
  evidence_event_ids: number[];
  dismiss_reason: string;
  dismiss_note: string | null;
  dismissed_at: string | null;
  dismissed_by: string;
}

export interface LearningDismissalsResponse {
  total: number;
  limit: number;
  offset: number;
  rows: LearningDismissalRow[];
}

export interface LearningEditRow {
  id: number;
  deal_kind: string;
  deal_key: string;
  field_name: string;
  proposed_value: unknown;
  user_value: unknown;
  proposed_decision: string | null;
  proposed_confidence: string | null;
  aggregator_run_id: number | null;
  prompt_version_id: number | null;
  edited_at: string | null;
  edited_by: string;
}

export interface LearningEditsResponse {
  total: number;
  limit: number;
  offset: number;
  rows: LearningEditRow[];
}

// =====================================================================
// Prompts admin (Slice L1/L2)
// =====================================================================

export interface PromptVersionSummary {
  id: number;
  purpose: string;
  version_tag: string;
  is_active: boolean;
  notes: string | null;
  created_by: string;
  created_at: string;
}

export interface PromptVersionDetail extends PromptVersionSummary {
  system_prompt: string;
  user_prompt_template: string | null;
  tool_definitions_json: string | null;
}

export interface PromptDiffResponse {
  a_id: number;
  a_version_tag: string;
  b_id: number;
  b_version_tag: string;
  unified_diff: string;
}

export interface PromptInvocationSummary {
  id: number;
  prompt_version_id: number;
  purpose: string;
  version_tag: string;
  invoked_at: string;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
}

export interface PromptInvocationDetail {
  id: number;
  prompt_version_id: number;
  purpose: string;
  version_tag: string;
  invoked_for_kind: string;
  invoked_for_id: number;
  model: string;
  invoked_at: string;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  rendered_system: string;
  rendered_user: string;
  raw_response: string | null;
}

export interface CreatePromptVersionPayload {
  purpose: string;
  version_tag: string;
  system_prompt: string;
  notes?: string | null;
  user_prompt_template?: string | null;
  tool_definitions_json?: string | null;
  activate_immediately?: boolean;
}

// --- NDA Reviewer tab (build plan 1.11, spec §12.1) — read-only Phase 1 ---
// Mirrors backend schemas.py Nda* models.

// PR-1: waiting-on is now the event-sourced projection facet. Nullable only for
// a review with no lifecycle events yet (the synthesis seed backfills existing
// reviews; new reviews get an intake event transactionally).
export type NdaWaitingOn = 'system' | 'ember' | 'counterparty' | 'none';
export type NdaPhase =
  | 'intake' | 'first_review' | 'negotiation' | 'execution' | 'complete';
export type NdaAttention = 'clear' | 'needs_attention' | 'blocked';

export interface NdaDocketRow {
  id: number;
  status: string;
  deal_name: string;
  deal_key: string | null;
  counterparty_org_id: number | null;
  // PR-2: resolved display labels (counterparty name from the nda_review column;
  // counsel name batch-resolved from the CRM org layer).
  counterparty_display_name: string | null;
  counterparty_law_firm_name: string | null;
  role_framing: string | null;
  latest_turn_number: number | null;
  latest_turn_status: string | null;
  waiting_on: NdaWaitingOn | null;
  must_have_open: number;
  must_have_total: number;
  created_at: string;
  age_days: number;
  last_activity_at: string | null;
  archived_at: string | null;
  archived_by: string | null;
  // Observability (slice 1): origin + back-link.
  triggered_by: string | null;
  source_event_log_id: number | null;
  // Delivery truth (PR 0A): honest initial-packet state + resolved recipients.
  initial_delivery_state: NdaDeliveryState | null;
  delivery_reason: NdaDeliveryReason | null;
  packet_to: string[];
  packet_cc: string[];
  // Lifecycle projection facets (PR-1: single source of status/phase/waiting-on).
  current_phase: NdaPhase | null;
  attention_state: NdaAttention | null;
  internal_owner_email: string | null;
}

// `failed` is intentionally not emitted yet — it lands in PR 3 with the
// lifecycle `packet_failed` projection (see mailer.derive_initial_delivery_state).
export type NdaDeliveryState = 'not_ready' | 'queued' | 'delivered';
export type NdaDeliveryReason = 'brad_only' | 'reply_sender' | 'pipeline_gc_policy';

export interface NdaReviewSource {
  event_log_id: number;
  signal_id: number | null;
  subject: string | null;
  sender: string | null;
}

// Result of POST /api/nda/reviews/{id}/bind|unbind (slice 3 manual bind).
export interface NdaBindResponse {
  id: number;
  deal_kind: string | null;
  deal_key: string | null;
  deal_name: string;
}

// Result of PATCH /api/nda/reviews/{id}/status (manual lifecycle picker).
export interface NdaStatusResponse {
  id: number;
  status: string;
}

// PR-2b: CRM org typeahead + manual link editing.
export interface NdaOrgSearchResult {
  org_id: number;
  name: string;
  domain: string | null;
}

export interface NdaOrgSearchResponse {
  results: NdaOrgSearchResult[];
}

export interface NdaLinksUpdate {
  counterparty_org_id?: number | null;
  counterparty_law_firm_org_id?: number | null;
  company_org_id?: number | null;
  counterparty_display_name?: string | null;
  role_framing?: string | null;
}

export interface NdaLinksResponse {
  id: number;
  counterparty_org_id: number | null;
  counterparty_law_firm_org_id: number | null;
  company_org_id: number | null;
  counterparty_display_name: string | null;
  counterparty_law_firm_name: string | null;
  role_framing: string | null;
  locked: string[];
}

export interface NdaDocketResponse {
  rows: NdaDocketRow[];
  archived_rows: NdaDocketRow[];
}

// GC auto-inclusion policy (PR 6, spec §0.4): forwarder_only | on_thread | always.
export interface NdaCaptureConfig {
  scan_outbound_enabled: boolean;
  scan_inbound_enabled: boolean;
  scan_execution_enabled: boolean;
  obligations_extraction_enabled: boolean;
  projection_enabled: boolean;
  notify_inbound_turn: boolean;
  notify_outbound_capture: boolean;
  notify_execution_candidate: boolean;
  notify_terms_extracted: boolean;
  authoritative_mailboxes: string[];
  notify_recipients: string[];
  turn_scan_lookback_days: number;
}

export interface NdaSettings {
  gc_auto_inclusion: string;
  capture: NdaCaptureConfig | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** PR-4 executed-copy candidate awaiting a human confirm. */
export interface NdaExecutionCandidate {
  work_item_id: number;
  message_id: number | null;
  filename: string | null;
  sender: string | null;
  subject: string | null;
  direction: string | null;
  cue_confidence: string | null;
  cue_signals: string[];
  source_permission_class: string | null;
  matched_review_id: number | null;
  matched_review_label: string | null;
  created_at: string | null;
}

export interface NdaExecutionCandidatesResponse {
  candidates: NdaExecutionCandidate[];
}

export interface NdaExecutionActionResponse {
  work_item_id: number;
  status: string;
  review_id: number | null;
  executed_document_id: number | null;
  backload: boolean;
  detail: string | null;
}

export interface NdaQueuesSummary {
  execution_candidates: number;
  outbound_candidates: number;
  turn_holds: number;
  intake_attach: number;
}

/** One open matter a held (intake-ambiguous) detected NDA might belong to (PR-8b). */
export interface NdaIntakeCandidateOption {
  review_id: number;
  deal_name: string;
  status: string;
  archived: boolean;
}

/** A detected NDA the matcher matched to >1 open matter, held for a human (PR-8b). */
export interface NdaIntakeCandidate {
  work_item_id: number;
  source_event_log_id: number | null;
  subject: string | null;
  via: string | null;
  deal_kind: string | null;
  deal_key: string | null;
  candidates: NdaIntakeCandidateOption[];
  created_at: string | null;
}

export interface NdaIntakeCandidatesResponse {
  candidates: NdaIntakeCandidate[];
}

export interface NdaIntakeActionResponse {
  work_item_id: number;
  status: string;
  review_id: number | null;
  turn_id: number | null;
  captured_document_id: number | null;
  detail: string | null;
}

// PR-8d: confident drain intake decisions (auto-binds) + reverse/re-route.
export interface NdaIntakeDecision {
  spine_id: number;
  kind: string | null; // bind | known_document | attach
  via: string | null;
  review_id: number | null;
  deal_name: string | null;
  turn_id: number | null;
  subject: string | null;
  from_email: string | null;
  decided_at: string | null;
  reverted_turn: boolean;
}

export interface NdaIntakeDecisionsResponse {
  decisions: NdaIntakeDecision[];
}

export interface NdaReverseDecisionResponse {
  spine_id: number;
  status: string;
  review_id: number | null;
  reversed_from_review_id: number | null;
  turn_id: number | null;
  alias_written: string[];
  detail: string | null;
}

/** Partial capture-config update — only provided keys are written. */
export type NdaCaptureConfigUpdate = Partial<NdaCaptureConfig>;

export interface NdaTurnRow {
  id: number;
  turn_number: number;
  direction: string;
  status: string;
  created_at: string;
}

export interface NdaFindingRow {
  id: number;
  guide_section: string;
  status: string;
  severity: string | null;
  draft_says: string | null;
  concern: string | null;
  recommendation: string | null;
  escalation_reason: string | null;
  edit_mode: string | null;
  anchor_text: string | null;
  anchor_occurrence: number | null;
  anchor_location_hint: string | null;
  replacement_text: string | null;
  comment_text: string | null;
  resolution: string;
  created_at: string;
  updated_at: string;
}

export interface NdaArtifactRow {
  id: number;
  turn_id: number | null;
  artifact_type: string;
  filename: string | null;
  sha256_short: string | null;
  ledger_hash_short: string | null;
  status: string | null;
  created_at: string;
}

export interface NdaTimelineEntry {
  occurred_at: string;
  kind: 'lifecycle' | 'email' | 'session' | 'audit';
  summary: string;
  // PR-5a: lifecycle entries carry the event type + a captured-document link.
  event_type?: string | null;
  document_id?: number | null;
  document_filename?: string | null;
  download_ok?: boolean | null;
  turn_id?: number | null;
}

/** PR-5a: one immutable document version on a review. */
export interface NdaDocument {
  id: number;
  version_number: number | null;
  version_kind: string | null;
  filename: string | null;
  direction: string | null;
  source: string | null;
  sha256_short: string | null;
  download_ok: boolean;
  is_executed: boolean;
  source_permission_class: string | null;
  created_at: string | null;
}

export interface NdaReviewDetail {
  id: number;
  status: string;
  deal_name: string;
  deal_kind: string | null;
  deal_key: string | null;
  role_framing: string | null;        // enum facet
  role_framing_text: string | null;   // the model's full sentence (prefer for display)
  counterparty_org_id: number | null;
  counterparty_law_firm_org_id: number | null;
  // PR-2: resolved display labels (see NdaDocketRow).
  counterparty_display_name: string | null;
  counterparty_law_firm_name: string | null;
  nda_processor_key: string | null;
  guidance_version_id: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  waiting_on: NdaWaitingOn | null;
  // Observability (slice 1): origin + back-link to the originating event.
  triggered_by: string | null;
  source: NdaReviewSource | null;
  // Delivery truth (PR 0A) — see NdaDocketRow.
  initial_delivery_state: NdaDeliveryState | null;
  delivery_reason: NdaDeliveryReason | null;
  packet_to: string[];
  packet_cc: string[];
  // Lifecycle projection facets (PR-1: single source) — see NdaDocketRow.
  current_phase: NdaPhase | null;
  attention_state: NdaAttention | null;
  internal_owner_email: string | null;
  turns: NdaTurnRow[];
  findings: NdaFindingRow[];
  artifacts: NdaArtifactRow[];
  timeline: NdaTimelineEntry[];
  // PR-5a: the immutable document version tree + the canonical executed copy.
  documents: NdaDocument[];
  executed_document_id: number | null;
}

// Executed-NDA closing review (lifecycle PR 6B-2). Mirrors backend
// NdaObligationRow / NdaExecutionSummaryRow / NdaExecutionDetail.
export interface NdaObligationRow {
  id: number;
  provision_key: string;
  binds: string | null;                 // ember | counterparty | mutual
  status: string;                       // extracted | confirmed | superseded | reverted
  obligation_summary: string | null;
  obligation_text: string | null;       // exact anchored quote (evidence; not editable)
  source_anchor: string | null;
  extraction_confidence: string | null;  // high | medium | low
  exists_flag: number | null;
  effective_date: string | null;
  expires_on: string | null;
  duration_label: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  edited: boolean;
}

export interface NdaExecutionSummaryRow {
  id: number;
  status: string;
  controlling_document_id: number | null;
  executed_on: string | null;
  executed_date_basis: string | null;
  counterparty_legal_name: string | null;
  ember_legal_name: string | null;
  signatory_name: string | null;
  signatory_title: string | null;
  side_represented: string | null;
  agreement_term: string | null;
  expiration_date: string | null;
  expiration_basis: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
}

export interface NdaExecutionDetail {
  review_id: number;
  lifecycle_status: string;
  execution_summary: NdaExecutionSummaryRow | null;
  obligations: NdaObligationRow[];
}

export interface NdaObligationActionResponse {
  review_id: number;
  affected: number;
  execution_summary: NdaExecutionSummaryRow | null;
  obligations: NdaObligationRow[];
}

// Fields a human may correct on edit-then-confirm (source evidence stays immutable).
export interface NdaObligationEdit {
  binds?: string;
  obligation_summary?: string;
  duration_label?: string;
  effective_date?: string;   // ISO date; "" clears
  expires_on?: string;       // ISO date; "" clears
  exists_flag?: number;
}

// First-turn calibration corpus (lifecycle PR 5B-2). Mirrors backend
// NdaCorpusRegion / NdaCorpusComparison / NdaCorpusComparisonResponse.
export interface NdaCorpusRegion {
  kind: string;               // added | deleted | replaced
  before_text: string;        // system-proposed side
  after_text: string;         // Peter-sent side
  guide_sections: string[];
  mechanical: boolean;        // typographic/whitespace-only (noise)
  attribution: string;        // tracked | inferred
  tracked_author: string | null;
  tracked_date: string | null;
}

export interface NdaCorpusComparison {
  id: number;
  review_id: number;
  turn_id: number;
  capture_status: string;     // complete | missing_* | diff_failed
  delta_hash: string | null;
  base_paragraphs: number | null;
  revised_paragraphs: number | null;
  substantive_change_count: number | null;
  regions: NdaCorpusRegion[];
  prompt_version_id: number | null;
  core_policy_version_id: number | null;
  model: string | null;
  scenario_profile_version_id: number | null;
  session_id: number | null;
  system_markup_artifact_id: number | null;
  system_markup_sha256: string | null;
  system_markup_ledger_hash: string | null;
  peter_sent_document_id: number | null;
  peter_sent_sha256: string | null;
  delta_sha256: string | null;
  delta_schema_version: string | null;
  annotation_status: string | null;
  created_at: string;
}

export interface NdaCorpusComparisonResponse {
  review_id: number;
  examples: NdaCorpusComparison[];
}

export interface NdaHealthResponse {
  poller_schedule_found: boolean;
  poller_last_run_at: string | null;
  poller_last_status: string | null;
  open_reviews: number;
  failed_sessions_7d: number;
  oldest_open_review_age_days: number | null;
  error_count_7d: number;
  // NDA-intent auto-detect 'armed awaiting draft' rows (intent seen, no draft
  // yet turned into a review). Non-zero that never converts = detection-broken.
  armed_awaiting_draft_count: number;
  // NDA PR 2 .doc converter reachability. null = feature off / not applicable
  // (render neutral, not an alarm); true/false only when the flag is on.
  doc_converter_reachable: boolean | null;
}

export interface NdaErrorEntry {
  occurred_at: string;
  source: 'mailbox_event' | 'session';
  kind: string;
  review_id: number | null;
  from_email: string | null;
  subject: string | null;
  summary: string;
  detail: string | null;
}

export interface NdaErrorsResponse {
  errors: NdaErrorEntry[];
}

// ── LLM spend governance (admin) ────────────────────────────────────────────

export interface LlmPurposeBudget {
  purpose: string;
  cap_usd: number;
  today_usd: number;
  seven_day_usd: number;
  thirty_day_usd: number;
  auto_resume: boolean;
  paused: boolean;
  pause_reason: string | null;
}

export interface LlmOpenPause {
  purpose: string;
  reason: string;
  paused_at: string | null;
  today_cost_usd: number;
  cap_usd: number;
}

export interface LlmUsageOverview {
  today_usd: number;
  seven_day_usd: number;
  thirty_day_usd: number;
  global_ceiling_usd: number;
  by_purpose: LlmPurposeBudget[];
  thirty_day_breakdown: { purpose: string; calls: number; cost_usd: number }[];
  open_pauses: LlmOpenPause[];
  // The enrichment WEB (Brave/Exa) budget — the one non-LLM budget, kept beside the LLM caps so no
  // budget hides in an env var (owner rule: all budgets on the Admin page).
  web: { daily_cap_usd: number; today_usd: number; last7_usd: number; last30_usd: number };
  // Enrichment-queue defers, beside the budgets they get mistaken for. api_error = provider
  // failure/credit outage (admin-releasable); budget releases via purpose Resume; awaiting_org is
  // E-9's healthy org-first wait (self-clears).
  enrichment_defers?: { api_error: number; budget: number; awaiting_org: number; other: number;
    next_retry_at: string | null };
}

// Spend time-series for the admin bar chart. Buckets/series/purposes are dense +
// index-aligned: series[purpose][i] is the spend for buckets[i]. Mirrors the
// backend GET /api/admin/llm/spend-series response.
export type LlmSpendBucket = 'daily' | 'weekly' | 'monthly';
export type LlmSpendWindow = '30d' | '3mo' | '12mo' | 'custom';

export interface LlmSpendSeries {
  bucket: LlmSpendBucket;
  start: string;
  end: string;
  buckets: string[];
  purposes: string[];
  series: Record<string, number[]>;
  total_usd: number;
}

// ── CRM Admin → Privacy (personal-contacts list) ────────────────────────────
// Mirrors backend/app/crm/ingest/personal_screen._contact_row. The personal-contacts
// list is the SOLE suppression source for the deterministic ingest screen.
export type PersonalContactSegment = 'on_list' | 'proposed';

export interface PersonalContact {
  id: number;
  email: string;
  contact_name: string | null;   // resolved crm_person.primary_name; null pre-ER
  entry_source: 'user_defined' | 'system_proposed';
  validation_status: 'confirmed' | 'pending' | 'rejected';
  added_by: string | null;
  added_at: string | null;       // ISO
  hidden_items: number;          // approx messages + meetings involving the address
}

export interface PersonalContactsResponse {
  rows: PersonalContact[];
  total: number;
  page: number;
  page_size: number;
  counts: { on_list: number; proposed: number };
}

// ── CRM Admin → Ingest Health ───────────────────────────────────────────────
// Mirrors backend/app/crm/ingest/ops.crm_ingest_health.
export interface CrmSearchResponse {
  kind: string;
  query: string;
  // Meili "displayed" fields vary by index (content: subject/snippet/content_kind/…; directory:
  // primary_name/domain/entity_kind/…) — kept flexible; the panel renders the common ones.
  hits: Array<Record<string, unknown>>;
}

export interface IngestUnit {
  mailbox: string;
  source: string;
  claim_status: string | null;
  attempts: number;
  last_error: string | null;
  cursor_status: string | null;
  synced_at: string | null;
  updated_at: string | null;
}

export interface IngestDeadLetter {
  unit_key: string;
  attempts: number;
  last_error: string | null;
  updated_at: string | null;
}

export interface IngestWorker {
  in_flight: Array<{ unit_key: string; claimed_by: string | null; lease_expires_at: string | null }>;
  stalled: Array<{ unit_key: string; attempts: number; lease_expires_at: string | null }>;
  last_progress_at: string | null;
}

/**
 * One `crm_backfill_state` lane on the metadata-backload panel (spec §6 O1).
 * Mirrors backend/app/crm/ingest/ops._backload_panel — there is no Pydantic model, this IS the contract.
 */
export interface BackloadLane {
  mailbox: string;
  /** `crm_mail_backfill` | `crm_calendar_backfill` (+ two retired values on pre-migration rows). */
  lane: string;
  /** How deep the lane has walked. null = seeded, nothing claimed yet (the walk starts at "now"). */
  watermark: string | null;
  /** What the watermark MEANS on this lane — the calendar mark is the floor of the last COMPLETED
   *  window and real coverage can trail it by up to one window, so it must not be read as an exact
   *  position the way the mail lane's is. Server-supplied so the two can never drift apart. */
  watermark_label: string;
  /** The audited floor stamped by `POST /api/crm/admin/backload/seed`. Never null on a live row. */
  target: string | null;
  /** The PRE-walk corpus frontier from the audited seed's snapshot — "we expected ≈ this deep".
   *  null when no reset snapshot exists for the mailbox, or the estimator degraded. */
  expected_floor: string | null;
  /** watermark − expected_floor, in days. POSITIVE = stopped SHALLOWER than the corpus already
   *  reached (the "short result that looks like success"); negative = went deeper than expected. */
  floor_delta_days: number | null;
  /** 0-100 down the audited range, or null when nothing has been walked yet. */
  progress_pct: number | null;
  /** A Graph continuation cursor is persisted for this lane. The link itself is never served. */
  resume_in_flight: boolean;
  status: string;
  /** 1 = Graph ran out ABOVE the target (the mailbox's real floor). Mail lane only — the calendar
   *  lane never sets it (an empty window is indistinguishable from a tenure gap). */
  reached_beginning: number;
  last_run_at: string | null;
  /** Clipped at 200 chars on both write and projection — Graph error text can quote a folder name. */
  last_error: string | null;
}

export interface BackloadPanel {
  rows: BackloadLane[];
  /** The audit-snapshot read failed ⇒ every `expected_floor` is null, rows still valid. */
  expected_floor_degraded: boolean;
  /** Mailboxes with an audited pre-walk snapshot on record. 0 with rows present = no expectation. */
  mailboxes_with_snapshot: number;
  /** The live lane vocabulary, so the FE never hardcodes it. */
  lanes: string[];
}

export interface CrmJobMetricSnapshot {
  job_name: string;
  run_id: number | null;
  captured_at: string | null;
  status: string;
  duration_ms: number | null;
  metrics: Record<string, unknown>;
}

export interface IngestHealth {
  queue: { pending: number; claimed: number; done: number; dead_letter: number };
  oldest_pending_claim_at: string | null;
  dead_letters: string[];
  dead_letter_details: IngestDeadLetter[];
  units: IngestUnit[];
  worker: IngestWorker;
  progress: { total_units: number; backfilled_units: number; partial_units: number; mailboxes: number };
  rate: { messages_last_hour: number; meetings_last_hour: number };
  config: {
    backfill_days: number;
    calendar_backfill_days: number;
    max_pages: number;
    lease_seconds: number;
    search_enabled: boolean;
    /** B8 retention posture. False (default) => `backlogs.deleted_unpurged` is the deliberately
     *  RETAINED corpus of soft-deleted messages, not a backlog — do not flag it. */
    purge_deleted_bodies: boolean;
    body_retention_days: number | null;
    schedule: { name: string; is_active: boolean; last_fired_at: string | null; last_status: string | null } | null;
    last_cycle: {
      at: string | null;
      status: string | null;
      chunks: number | null;
      chunk_limit: number | null;
      max_chunks_exhausted: boolean;
      remaining_partials: string[];
    } | null;
    /** S1 mailbox-wide sweep. The backend emitted these from the start but nothing rendered them, so the
     *  activation runbook pointed at a panel that could not show the allowlist. TypeScript's structural
     *  typing means extra server fields never fail a build, so no gate catches that drift — only a test
     *  asserting the rendered output does. */
    sweep_max_pages: number;
    sweep_backfill_days: number;
    /** page_size * max_pages = messages per chunk, so both are needed to read a chunk count. */
    sweep_page_size: number;
    /** Cap on mailboxes WORKED per fire (0 = no limit), so `mailboxes: N` can be below the allowlist size
     *  without anything failing. Mid-pass mailboxes are continued first and count toward the cap; only
     *  continuations can push the count above it. */
    sweep_mailboxes_per_cycle: number;
    /** Empty means the sweep REFUSES to run (not "all mailboxes"); '*' means every enrolled mailbox. */
    sweep_mailbox_allowlist: string;
    sweep_schedule: { name: string; is_active: boolean; last_fired_at: string | null; last_status: string | null } | null;
    sweep_last_cycle: {
      at: string | null;
      status: string | null;
      chunks: number | null;
      chunk_limit: number | null;
      max_chunks_exhausted: boolean;
      /** A pass that could not START its work (units mid-backfill but none claimable — usually leased to a
       *  worker killed by a restart) as opposed to one that ran out of budget. Distinct because reporting
       *  them the same way made a 300-chunk no-op read as success. */
      no_progress_units: string[];
      remaining_partials: string[];
    } | null;
  };
  cursors: Record<string, { cursors: number; oldest_sync_at: string | null }>;
  backlogs: {
    body: number;
    attachments: number;
    pending_screen: number;
    search_outbox: number;
    deleted_unpurged: number;
    /** Deleted bodies the recovery pass can still get back — what the next `CRM recover purged bodies`
     *  Fire-now will process. Derived from the pass's own predicate, so it cannot drift from it.
     *  ⚠ Does NOT fall when a user purges Deleted Items (the row is already tombstoned, so no delta fires
     *  and no column here changes) — a flat value is not evidence that nothing is being lost. */
    bodies_recoverable: number;
    /** Subset of `bodies_recoverable` already tried and unresolved (no resolvable mailbox, or a persistent
     *  fetch error). Non-terminal by design, so it is the floor `bodies_recoverable` cannot go below. */
    bodies_stuck: number;
    /** Deleted bodies recovery definitively could NOT get back (`unrecoverable_purged`). Permanent under
     *  the no-Tier-2 decision, so it only ever grows. */
    bodies_unrecoverable: number;
  };
  totals: { messages: number; meetings: number; directory_entities: number };
  search_enabled: boolean;
  job_metrics: {
    latest: CrmJobMetricSnapshot[];
    recent: CrmJobMetricSnapshot[];
  };
  storage: {
    sql: { used_mb: number; cap_mb: number | null } | null;
    meili: { database_size_bytes: number; indexes: Record<string, number> } | null;
  } | null;
  /** Slice O1 metadata-backload depth per (mailbox, lane). `null` = the block degraded (pre-migration
   *  table or any error) — the endpoint still 200s, so the card must render sanely for null AND for an
   *  empty `rows` list (nothing seeded yet). Optional so an older server response still type-checks. */
  backload?: BackloadPanel | null;
  as_of: string;
}

// ── CRM tab (prove-out read surfaces) ───────────────────────────────────────
// Mirrors the hand-built dicts in backend/app/routers/crm.py — there are no
// Pydantic response models on the CRM router; these types ARE the contract.

/** A `kind:'directory'` Meili hit (backend index_writer.build_directory_doc). */
/** person subtype (Ring-1 hygiene) — drives the People vs "Lists & shared mailboxes" split.
 * Missing/'person' = a real individual; the rest are non-person directory entities. */
export type CrmPersonSubtype = 'person' | 'distribution_list' | 'shared_mailbox' | 'automated';

export interface CrmDirectoryHit {
  doc_id: string; // 'person_7' | 'organization_9' | 'ember_entity_1'
  entity_kind: 'person' | 'organization' | 'ember_entity';
  entity_id: number;
  primary_name: string | null;
  domain?: string | null; // orgs only
  permission_class: string;
  subtype?: string; // person entities: CrmPersonSubtype; org/ember docs echo their entity_kind
  // person-row info line (search-quality spec Part 2) — display-only doc fields
  primary_email?: string | null;
  title?: string | null; // firm-wide ledger winner; enrichment-sourced only behind the pilot flag
  // person-row interaction stats + employer (spec Part 4, firm-wide-safe counts baked at index time)
  org_name?: string | null;
  emails_in?: number;   // messages they sent (role='from')
  emails_out?: number;  // outbound Ember messages naming them as a visible to/cc recipient
  meetings?: number;    // attended meetings (declined-everything invites excluded)
  last_contact_at?: number | null; // epoch seconds
}

/** A `kind:'content'` Meili hit — email/meeting subject+body, entitled-only. */
export interface CrmContentHit {
  doc_id: string; // 'email_123' | 'meeting_45'
  content_kind: 'email' | 'meeting' | 'note';
  content_id: number;
  subject?: string | null;
  snippet?: string; // always '' today (never populated)
  thread_id?: string | null;
  sent_at?: number; // unix epoch SECONDS (SQL surfaces return ISO strings — normalize!)
  permission_class: string;
}

/** GET /api/crm/person/{id} | /organization/{id} — the raw governed row + dup count. */
export interface CrmEntityProfile {
  id: number;
  primary_name: string | null;
  primary_email?: string | null; // person
  primary_domain?: string | null; // organization
  alt_domains_json?: string | null; // organization (JSON-encoded list)
  permission_class: string;
  merged_into_id: number | null;
  status: string | null;
  subtype?: string | null; // person entities only (CrmPersonSubtype); NULL = unclassified→person
  created_at: string | null;
  updated_at: string | null;
  possible_duplicate_count: number;
}

/** PM-9 — the entity-profile merge modal (person-merge spec §7). All three are READS; the merge
 *  itself is POST /api/crm/admin/{person,org}-merge, unchanged.
 *  ⚠ The kind is spelled as a literal union rather than importing `CrmEntityKind` from
 *  `hooks/useCrm` — that module imports THIS one, so the reference would be a cycle. */
export type CrmMergeKind = 'person' | 'organization';

export interface CrmMergeCandidate {
  kind: CrmMergeKind;
  id: number;
  name: string | null;
  primary_email?: string | null;   // person
  primary_domain?: string | null;  // organization
  permission_class: string;
  merged_into_id: number | null;
  created_at: string | null;
  /** Person rows only: 'person' | 'distribution_list' | 'shared_mailbox' | null. A non-person subtype
   *  is NOT refused by the merge engine, so the picker has to label it. */
  subtype?: string | null;
  /** A login may be the SURVIVOR of a grant-free merge (R14b) but never the merged-away side —
   *  the modal refuses the ROLE, not the record. */
  is_login: boolean;
  /** Interactions the CALLER can see (a scoped read — two admins can see different numbers). */
  interactions_visible: number | null;
  /** Non-null when this pair is already an open duplicate-review item, e.g. 'co_org_local_part'. */
  already_queued_basis: string | null;
  suggested: boolean;
}

export interface CrmMergeRole {
  key: string;
  label: string;
  enabled: boolean;
  why?: string;
}

/** ── The NATIVE deal merge (entry → entry, E2d-3's surface) ────────────────────────────────────
 *
 *  Distinct from `CrmMergeCandidate` above, which is a PERSON or an ORGANIZATION. This one is a DEAL:
 *  a `crm_list_entry` on a native list. The two merges share a discipline and share no code path. */
export interface CrmDealMergeTarget {
  entry_id: number;
  name: string;
  /** The row's stage. Terminal stages ARE offered (the owner's resurrection ruling) and ranked last,
   *  so this is what lets the operator see a target is terminal before choosing it. Always null on an
   *  AI proposal — its stage lives in governed proposal content the picker never reads. */
  stage: string | null;
  /** A `proposed` target is another AI draft, offered only when the source is a draft too (owner
   *  ruling R1, 2026-10-26: draft→draft is allowed, committed→draft never is). */
  lifecycle_status: 'proposed' | 'committed';
}

/** One field where both deals hold a human-entered value and the values DIFFER. Equal values are not
 *  conflicts — they are one fact recorded twice. Every conflict must be decided or the merge refuses:
 *  "the merge picked one" is indistinguishable from data loss to whoever's edit vanished. */
export interface CrmDealMergeConflict {
  table: string;
  key: Record<string, unknown>;
  survivor_value: unknown;
  source_value: unknown;
  /** The server's OWN key for this conflict (`deal_merge._conflict_key`) — send it back verbatim as
   *  the `conflict_choices` key. Never rebuild it client-side: a second spelling of an identity key is
   *  how the `'opp'` typo silently no-op'd a whole dual-write once already. */
  choice_key: string;
}

export interface CrmDealMergePreview {
  source_entry_id: number;
  survivor_entry_id: number;
  source_name: string;
  survivor_name: string;
  survivor_stage: string | null;
  source_lifecycle_status: string | null;
  /** `proposed` when the survivor is itself an AI draft: it stays one proposal under review after the
   *  merge (never auto-accepted), to be Accepted / Passed / Dismissed later as one record. */
  survivor_lifecycle_status: 'proposed' | 'committed' | null;
  /** `table.column` → how many rows would move. Only non-zero entries are present. */
  row_counts: Record<string, number>;
  identity_signals: number;
  conflicts: CrmDealMergeConflict[];
  /** G12 (owner ruled 2026-08-28: the audience UNIONS). `loses` is normally empty; it is non-empty in
   *  exactly one shape — a principal granted on one side and actively DENIED on the other, where the
   *  deny wins — which is the case the ruling's plain wording does not cover. */
  access: {
    gains: number[];
    loses: number[];
    keeps: number[];
    widens_only: boolean;
  };
}

export interface CrmMergeCandidatesResponse {
  self: CrmMergeCandidate;
  kind: CrmMergeKind;
  query: string;
  candidates: CrmMergeCandidate[];
  index_is_behind: boolean;
  roles: CrmMergeRole[];
}

export interface CrmMergeHistoryResponse {
  kind: CrmMergeKind;
  id: number;
  merge: {
    merge_log_id: number;
    survivor_id: number | null;
    merged_away_id: number | null;
    reason: string | null;
    evidence_note: string | null;
    merged_by: string | null;
    merged_at: string | null;
    undone_at: string | null;
    this_record_was: 'survivor' | 'merged_away';
  } | null;
  undo: {
    eligible: boolean;
    reason_code: string | null;
    message: string;
    rows_to_reverse?: number;
  };
}

/** What the operator must TYPE to arm — served by the preview so the browser holds no second copy
 *  of "when are two names the same" (`field: null` = this pair cannot be armed here). */
export interface CrmMergeConfirmField {
  field: 'name' | 'primary_email' | 'primary_domain' | null;
  expected: string | null;
  label: string;
  why?: string;
}

/** GET /api/crm/email/{id}/meta — the metadata projection for one email (body-free;
 * extra projection columns like internet_message_id may ride along untyped). */
export interface CrmEmailMeta {
  id: number;
  subject: string | null; // null can mean SEALED (body_gated, not on thread)
  sent_at: string | null;
  // Ember's perspective ('email_sent' | 'email_received'), from the shared backend taxonomy — the
  // SAME field the timeline chip reads, so the two surfaces cannot disagree.
  activity_type?: string | null;
  // PROVENANCE only: which mailbox folder the sighting that created the row came from. Decided by
  // whichever mailbox the crawler reached first, so it does NOT answer "did the firm send this" —
  // render `activity_type` instead (mail-direction-semantics-spec §4).
  provenance_direction: string | null;
  thread_id: string | null;
  from_person_id: number | null;
  permission_class: string;
}

/** GET /api/crm/email/{id}/content — the governed content-status envelope
 *  (`docs/specs/strategic-architecture/governed-email-content-retrieval-spec.md` §9).
 *
 *  ⛔ It deliberately carries NO storage ref, provider/Graph id or mailbox identity. One appearing
 *  on the wire is a backend defect, not a type to widen. */
export type CrmContentState =
  | 'ready'          // stored, gate passed, no quarantine signal
  | 'blocked'        // quarantined / Defender `Malicious` — "Removed by security scanning"
  | 'scan_pending'   // the malware verdict is not readable yet — retryable, never clean
  | 'not_stored'     // manifest-only: unsupported type / oversize / never captured
  | 'not_captured'   // nothing enumerated or stored yet
  | 'fetching'       // an acquisition is in flight (ours or another reader's)
  | 'retry_later'    // the source said "not now" — the server supplies the delay
  | 'unavailable'    // gone from every eligible copy, or a reference with no object
  | 'empty'          // an AUTHORITATIVE negative ("No document attachments")
  | 'partial';       // rows known, enumeration not complete — never "empty"

export interface CrmEmailAttachmentItem {
  id: number;
  name: string | null;
  /** Normalized bare media type (parameters stripped, lower-cased), or null when unknown. */
  content_type: string | null;
  size_bytes: number | null;
  state: CrmContentState;
  /** SERVER-derived, never inferred client-side: preview is PDF-only and only when ready. */
  can_preview: boolean;
  can_download: boolean;
  /** EC-5. SERVER-derived eligibility for retrieving THIS file from Microsoft 365 on a click.
   *  The client never decides this — see `content_acquisition.fetch_eligibility`. */
  can_fetch?: boolean;
  retry_after_seconds?: number | null;
}

export interface CrmEmailContent {
  email_id: number;
  body: {
    state: CrmContentState;
    /** EC-5, server-derived: the on-demand flag, governance, deletion, quarantine, the retry
     *  window, a live claim held elsewhere and the negative cache all fold into this one boolean. */
    can_fetch: boolean;
    retry_after_seconds: number | null;
  };
  attachments: {
    state: CrmContentState;
    /** True only when the enumeration is known COMPLETE. `false` with items = partial. */
    complete: boolean;
    checked_at: string | null;
    /** Whether the MANIFEST can be enumerated on demand. Per-file bytes are on each item. */
    can_fetch?: boolean;
    retry_after_seconds?: number | null;
    items: CrmEmailAttachmentItem[];
  };
  request_id: string;
  /** Present only on the RECORD-context door: the authorized event this envelope was read for. */
  context?: { list_id: number; entry_id: number; event_log_id: number };
}

/** POST …/content/fetch — one bounded acquisition's typed outcome. Never bytes, never a URL. */
export interface CrmContentFetchResult {
  email_id: number;
  resource: 'body' | 'manifest' | 'attachment';
  state: CrmContentState;
  reason: string | null;
  retry_after_seconds: number | null;
  complete: boolean | null;
  request_id: string;
}

export interface CrmTimelineParticipant {
  email_address: string | null;
  display_name: string | null;
  resolved_person_id: number | null;
  /** `crm_message_participant.role` is NULLABLE and not a closed enum on the wire — ingest writes
   *  from/to/cc/bcc today, but a row with a null or unrecognized role must still RENDER (F.2 review
   *  minor 6: banding on a hardcoded four made such rows vanish out of the reveal entirely). */
  role: string | null;
  /** Pillar F.2: set (never `false`) when this person's PROFILE is gated — internal-profile-visibility
   *  §3.3. Absent = linkable. The NAME always renders either way; only the link is withheld. */
  profile_hidden?: boolean;
}

export interface CrmTimelineAttendee {
  email_address: string | null;
  display_name: string | null;
  resolved_person_id: number | null;
  response_status: string | null;
  /** See CrmTimelineParticipant.profile_hidden. */
  profile_hidden?: boolean;
}

/** Pillar F.2 — `/crm/meeting/{id}/attendees`, fetched when a calendar timeline row is expanded. */
export interface CrmMeetingAttendeesResponse {
  attendees: CrmTimelineAttendee[];
  organizer_person_id: number | null;
  subject: string | null;
}

/** Pillar F.2 — `/crm/email/{id}/participants`, fetched when a mail timeline row is expanded. */
export interface CrmEmailParticipantsResponse {
  participants: CrmTimelineParticipant[];
}

/** Pillar F.2 — `/crm/person/{id}/card`, the hovercard tile. `title` reads the same source the profile
 *  page renders (the reconciled signature card), never the unfilled deal-panel field (the D-17 lesson). */
export interface CrmPersonCard {
  id: number;
  name: string | null;
  title: string | null;
  firm: string | null;
}

/** One merged timeline row. `subject: null` can mean SEALED (body_gated, not on thread); notes carry
 *  no subject at all (they render `preview` instead). */
export interface CrmTimelineRow {
  kind: 'email' | 'meeting' | 'note';
  id: number;
  subject?: string | null;
  occurred_at: string | null; // ISO
  // normalized activity-type tag (Item C taxonomy): 'email_sent' | 'email_received' | 'meeting' | 'note'.
  // Drives the Timeline-v2 type+date filter (D6/E3) AND the sent/received chip — for email it is the
  // FIRM-PERSPECTIVE answer (an internal `from` address), the only field that answers it.
  activity_type?: string | null;
  // email-only. PROVENANCE: which mailbox folder the first sighting came from — NOT "did the firm
  // send this" (mail-direction-semantics-spec §4). Kept on the wire for debugging; nothing renders it.
  provenance_direction?: string | null;
  thread_id?: string | null;
  from_person_id?: number | null;
  participants?: CrmTimelineParticipant[];
  // meeting-only
  ended_at?: string | null;
  organizer_person_id?: number | null;
  attendee_count?: number;
  attendees?: CrmTimelineAttendee[];
  // note-only (Item E / E3): preview IS body content (governed — an unreadable note is omitted, never
  // sealed). updated_at drives the "edited" affordance (an edit does not reorder the row). `links` are
  // the note's OTHER tagged entities (governed, subject-withheld; the timeline entity itself excluded).
  preview?: string | null;
  author_person_id?: number | null;
  author_name?: string | null;
  updated_at?: string | null;
  links?: CrmTimelineTag[];
  /** email-only (manual email-to-record link, spec §3.1/§9). SERVER-COMPUTED: true only when the
   *  message is metadata-visible AND the governed content-axis body query authorizes this principal.
   *  The client never infers it — a non-null subject, an admin-looking role or deal-team membership
   *  are all insufficient. Absent/false ⇒ no Link affordance, and no reason is disclosed. */
  can_link_to_record?: boolean;
  /** email-only (ML-7). The deals this email ALREADY stands as evidence for — what turns the faint
   *  **Link** affordance into a solid **Linked** chip. SERVER-GATED: a deal the caller may not see is
   *  absent from the array entirely, with no placeholder and no count, so the length IS the number of
   *  deals this caller may open and never "how many exist". Absent (an older backend) or empty ⇒ the
   *  row renders exactly as it did before ML-7. */
  linked_records?: CrmTimelineLinkedRecord[];
}

/** One deal an email is already evidence for (ML-7, spec §3.1/§3.3). Exactly the picker's target
 *  shape minus the pipeline/list labels the chip has no room for — no event id, no resolution
 *  method, no binding count: the chip's job is to NAME deals the caller may open. */
export interface CrmTimelineLinkedRecord {
  list_id: number;
  entry_id: number;
  name: string;
  stage: string | null;
  lifecycle_status: 'committed' | 'proposed';
}

/** One governed target in the manual email-to-record link picker (spec §7.1). Typed on purpose: v1
 *  serves only the IOT `deal_pipeline` list, but adding a second pipeline is a provider change on the
 *  server, not a modal rewrite — the modal reads `list_name`/`pipeline_kind` rather than hardcoding
 *  “IOT”. Only full-row-visible proposed records ever appear here; hidden/name-only rows are absent
 *  from the payload entirely (they do not leak through counts or ranking). */
export interface CrmEvidenceLinkTarget {
  pipeline_kind: 'deal';
  list_id: number;
  entry_id: number;
  name: string;
  stage: string;
  lifecycle_status: 'committed' | 'proposed';
  list_name: string;
}

export interface CrmEvidenceLinkTargetsResponse {
  targets: CrmEvidenceLinkTarget[];
}

/** The typed target reference the link command takes and echoes back (spec §7.2). */
export interface CrmEvidenceLinkTargetRef {
  pipeline_kind: 'deal';
  list_id: number;
  entry_id: number;
}

/** `POST /api/crm/email/{message_id}/record-links` (spec §7.2). `created`/`already_linked` count ONLY
 *  messages the caller was entitled to link — they never reveal a hidden thread-member count, which is
 *  why the success copy may quote them verbatim. `scope` is the SERVER's answer: a message with no
 *  canonical thread degrades to `message` even if `thread` was requested. */
export interface CrmLinkEmailToRecordResponse {
  status: string;
  operation_id: string;
  scope: 'message' | 'thread';
  target: CrmEvidenceLinkTargetRef;
  created: number;
  already_linked: number;
  /** Null when classification will enqueue the aggregate after its attempt, or when an exact
   * already-linked repeat changed no effective evidence. */
  aggregate_job: { job_id: number; status: string; deduped: boolean } | null;
}

/** A tagged entity on a note timeline row (Item E / E3) — resolved + governed (withheld → omitted). */
export interface CrmTimelineTag {
  kind: string; // person | organization | ember_entity | list_entry
  id: number;
  name: string;
}

export interface CrmTimelineResponse {
  rows: CrmTimelineRow[];
  has_more: boolean;
  offset: number;
  limit: number;
}

/** One activity-type in the shared taxonomy (Item C) — the Timeline-v2 filter vocabulary. */
export interface CrmActivityType {
  key: string;
  label: string;
  category: string;
}

export interface CrmActivityTypesResponse {
  types: CrmActivityType[];
}

export interface CrmEmploymentRow {
  org_id: number | null;
  org_name: string | null;
  title: string | null;
  seniority: string | null;
  role: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_current: boolean;
}

export interface CrmEmploymentResponse {
  group?: string | null;
  rows: CrmEmploymentRow[];
  signature: {
    title: string | null;
    division: string | null;
    office: string | null;
    phone: string | null;
    linkedin: string | null;
    confidence: string | null;
  } | null;
  aliases: string[];
}

/** `POST /api/crm/person/{id}/employment` — a hand-recorded affiliation (⋯ → Add role), for the roles
 *  entity resolution can never infer because the person does not mail from the company's domain.
 *  `created` is false when an existing OPEN row for the same (person, org) was UPDATED rather than a
 *  second, duplicate affiliation inserted. */
export interface CrmAddEmploymentResult {
  id: number;
  employment_id: number;
  org_id: number;
  title: string;
  role: string | null;
  created: boolean;
}

export interface CrmOrgPersonRow {
  division?: string | null;
  group?: string | null;
  person_id: number;
  name: string | null;
  email: string | null;
  title: string | null;
  is_current: boolean;
  interactions: number; // distinct emails + meetings the person is on (caller-scoped)
  emails_in: number; // messages sent by the person
  emails_out: number; // outbound Ember messages naming them as a visible to/cc recipient
  meetings: number;
  last_contact_at: string | null;
  subtype: string; // 'person' | 'distribution_list' | 'shared_mailbox' | 'automated'
  /** internal-profile-visibility-spec §3.3 — the row renders as plain text instead of a click-through
   *  when true, because that person's profile 404s for this caller. The ROW still renders: an org's
   *  member list is names, not profiles (§3.4). */
  profile_hidden?: boolean;
}

export interface CrmOrgPeopleResponse {
  rows: CrmOrgPersonRow[];
  domains: string[];
  // active org_short_name aliases (e.g. "SIC", "NMSIC") — enrichment-derived acronyms/short brands.
  aliases: string[];
  // shared/functional mailboxes at the org's domains (crm_shared_address — reclassified boxes that
  // resolve org-only, no person); rendered in the "Lists & shared mailboxes" section.
  shared_mailboxes?: { email: string }[];
}

/** One observation from the attribute ledger (crm_entity_attribute) — enrichment→profile integration §6. */
export interface CrmAttrObservation {
  value: string | null;
  source: string; // 'user' | 'enrichment' | 'resolver' | 'merge'
  actor: string | null;
  confidence: string | null;
  has_evidence: boolean; // presence only — the raw source pointer is redacted server-side (review F5)
  enrichment_run_id: number | null;
  reverted_at: string | null;
  observed_at: string | null;
}

export interface CrmAttrHistory {
  current: CrmAttrObservation | null; // projected winner (excl. reverted)
  history: CrmAttrObservation[]; // newest-first, incl. reverted
}

/** GET /api/crm/lists/{id}/entries/{eid}/fields/{key}/history — CUT-8's per-field version stream,
 *  newest first. `divergence` is non-null only when the stream head disagrees with the rendered
 *  value, which is legitimate after a rollback to Affinity (the import overwrites the cell and
 *  correctly appends nothing to the stream). */
export interface CrmFieldHistoryEntry {
  version: number;
  value: unknown;
  event_kind: string;
  actor: string | null;
  method: string | null;
  occurred_at: string | null;
}

export interface CrmFieldHistoryResponse {
  field_key: string;
  entries: CrmFieldHistoryEntry[];
  current: unknown;
  divergence: { stream_head: unknown; current: unknown; reason: string } | null;
}

/** GET /api/crm/{kind}/{id}/attribute-history — per-key current winner + full provenance/history. Powers
 *  the profile ✦/history affordance AND the inline render of the ledger-backed T1 identity facts. */
export interface CrmAttrHistoryResponse {
  entity_kind: string;
  entity_id: number;
  attributes: Partial<
    Record<'display_name' | 'title' | 'seniority' | 'division' | 'group' | 'linkedin' | 'org_types', CrmAttrHistory>
  >;
}

/** Derived interaction-based connection (never curated — crm_relationship is empty). */
export interface CrmConnectionRow {
  person_id: number;
  name: string | null;
  email: string | null;
  emails: number;
  meetings: number;
  total: number;
  last_at: string | null;
}

export interface CrmConnectionsResponse {
  rows: CrmConnectionRow[];
}

// ── Entity-profile LINKED RECORDS (entity-profile-linked-records-spec.md §7). The heuristic
// `matched_by` bridge these replaced is deleted: a linked record is an ACTIVE RELATIONSHIP FACT
// whose `to` endpoint is a list entry the caller may see. Mirrors backend/app/crm/linked_records.py.

/** One role the entity holds on the record. `label` is rendered from the GOVERNED VOCABULARY
 * server-side — the FE holds no role vocabulary and must never map `relationship_type` to prose. */
export interface CrmLinkedRecordRole {
  label: string;
  relationship_type: string;
  qualifiers: Record<string, unknown>;
  /**
   * Party-facts spec §5 rule 2 — another chip on this SAME row says something that entails this one
   * (an adviser fact beside a bare correspondence fact), so the card hides it behind "+N implied".
   *
   * ⚠ **Hidden, never withheld, and never a gate.** The fact is active, its evidence still counts in
   * `evidence`, and the row always renders — folding is per chip, never per row. Absent when
   * `crm_party_display_fold_enabled` is off, which is why it is optional rather than defaulted false:
   * the client rule stays the fallback for a payload that predates the flag.
   */
  implied?: boolean;
}

/** Evidence provenance — the replacement for the heuristic chip. `decided_by` is null when a human
 * ruled but the caller may not see them (§4's entity-side symmetry: the row renders, the party it
 * may not name is omitted); `producers` names the machine producer class when nobody has ruled. */
export interface CrmLinkedRecordEvidence {
  messages: number;
  first_at: string | null; // the evidence LINK dates, not the message dates (see the service)
  last_at: string | null;
  decided_via: 'manual' | 'machine';
  decided_by: string | null;
  producers: string[];
  /**
   * WP-6b — the facts this line rolled up, so the "N messages" text can open the "why?" popover.
   *
   * Present ONLY while `crm_fact_evidence_trace_enabled` is on, which is why it is optional: a dark
   * payload is byte-identical to pre-PR-F, and the card renders the line as plain text when the key
   * is absent rather than a button that 404s.
   */
  assertion_ids?: number[];
}

export interface CrmLinkedRecordRow {
  entry_id: number;
  lifecycle_status: string; // 'committed' | 'proposed'
  record_ref: { kind: string; id: number };
  /** Present only where the identity seam resolves one — then the row deep-links into the Deals tab;
   * otherwise the row opens the CRM record panel over its list. */
  deal_ref: { deal_kind: string; deal_key: string } | null;
  /** ⛔ On a PROPOSED row this carries name + nothing else, by the D8 cap (§4) — the FE renders what
   * it is given and never asks for more. `numeric` arrives RAW so the card formats it once. */
  display: { primary: string; secondary: string; numeric: string | number | null };
  roles: CrmLinkedRecordRole[];
  evidence: CrmLinkedRecordEvidence;
}

/** One group per list, and the group is the unit the LIST gate acts on: a list the caller cannot
 * access is ABSENT — not empty, not counted (§4). `row_count` is the group's true size before the
 * per-list `max_rows` cap. */
export interface CrmLinkedRecordGroup {
  list_id: number;
  list_name: string;
  label: string; // the list's configured card heading (config_json.entity_profile_card.label)
  row_count: number;
  rows: CrmLinkedRecordRow[];
}

export interface CrmLinkedRecordsResponse {
  groups: CrmLinkedRecordGroup[];
}

// ── Native Lists (Domain E — build plan M0/M1). No Pydantic response models on the CRM
// router; these types ARE the contract (keep in sync with backend/app/crm/lists.py). ──
export interface CrmListSummary {
  id: number;
  name: string;
  list_kind: string;
  permission_class: string;
  entry_count: number; // D8: COMMITTED entries only (proposed rows are counted separately)
  proposed_count?: number; // D8: whole-record proposals awaiting Accept/Dismiss (the "+K proposed" badge)
  template_key?: string | null;
  entry_subject_kind?: string | null; // 'person' | 'organization' | null (=opportunity)
  derived?: string[];
}

export interface CrmListsResponse {
  lists: CrmListSummary[];
}

// ── list sharing v1 (GET/POST /crm/lists/{id}/shares, DELETE …/shares/{share_id}) ─────────────
// ONE access level, FULL: an un-revoked share makes the holder functionally the owner (see, edit,
// share, revoke, leave). Deletion stays owner-only; the permission_class ceiling is absolute, so a
// grant can come back `denied_by_ceiling` and that is a final answer, not a retryable error.
export type CrmShareOutcome =
  | 'granted' | 'already_shared' | 'owner' | 'denied_by_ceiling'
  /** the 2026-08-27 internal-only ruling: a real directory row, but not an Ember login-holder */
  | 'not_internal_user' | 'unknown_address';

/** One person the Share typeahead may offer — the ACTIVE Ember roster, NOT the contact directory
 *  (`GET /crm/lists/{id}/share-candidates`). v1 sourced the typeahead from `/crm/lookup` and therefore
 *  offered every counterparty in the CRM; see the list-sharing spec §6. */
export interface CrmShareCandidate {
  person_id: number;
  name: string | null;
  email: string;
  /** false = they hold no clearance for this list's class, so Share would answer denied_by_ceiling.
   *  Still offered, WITH the reason — hiding them would hide the fix (a clearance grant). */
  within_ceiling: boolean;
}
export interface CrmShareCandidatesResponse {
  list_id: number;
  candidates: CrmShareCandidate[];
}

export interface CrmListShare {
  share_id: number;
  person_id: number;
  name?: string | null;   // null = a directory row this caller's class can't read (render restricted)
  email?: string | null;
  /** null when the granter's own directory row is unreadable to this caller (masked server-side to
   *  the same standard as name/email); a non-address provenance token passes through. */
  granted_by?: string | null;
  granted_at?: string | null;
  access?: string;        // 'view' | 'edit' — carried, NOT a gate in v1 (see the spec's tiering note)
  /** false = the row is live but axis-1 no longer admits its holder, so they cannot open the list
   *  (a clearance was withdrawn). Still revocable, and re-effective if the class is restored. */
  effective?: boolean;
  /** false = the internal-only ruling (spec §2.5) would no longer GRANT this row — an external, or a
   *  colleague who has since left the firm. The row is still LIVE (the read overlay honors it), which
   *  is why it is named rather than silent. Undefined on an older payload = no concern. */
  internal?: boolean;
}

export interface CrmListSharesResponse {
  list_id: number;
  list_name: string;
  permission_class: string;
  owner: { person_id: number; name?: string | null; email?: string | null };
  /** Never includes the owner's own (redundant) share rows — see `owner_share_rows`. */
  shares: CrmListShare[];
  can_share: boolean;
  caller_person_id: number;
  /** >0 = the owner also holds share row(s) on their own list (an importer artefact). Not rendered
   *  as access — the owner row already is — but reported so the state is never invisible. */
  owner_share_rows?: number;
}

export interface CrmShareGrantResult {
  email: string;
  outcome: CrmShareOutcome;
  person_id?: number;
}

/** POST response: the per-address outcomes PLUS the refreshed access table (no extra round-trip). */
export interface CrmShareGrantResponse extends CrmListSharesResponse {
  results: CrmShareGrantResult[];
  counts: Record<CrmShareOutcome, number>;
}

export interface CrmShareRevokeResponse {
  revoked: number;
  share_id: number;
  person_id: number;
  /** true = the caller removed THEMSELVES; the list is no longer theirs to read. */
  self: boolean;
  shares: CrmListShare[] | null;   // null on a self-revoke — there is no table left to show
}

// L1 creation wizard — the template catalog (GET /list-templates).
export interface CrmListTemplate {
  key: string;
  label: string;
  type: 'opportunity' | 'person' | 'organization';
  forces_class: string | null; // e.g. 'team_ir' for Investor Relations
  fields: { field_key: string; label: string; field_type: string }[];
  derived: string[];
}
export interface CrmListTemplatesResponse {
  templates: CrmListTemplate[];
}

// L1 person/org membership.
export interface CrmMemberLookupResult {
  subject_kind: 'person' | 'organization';
  subject_id: number;
  name: string;
}
export interface CrmMemberLookupResponse {
  results: CrmMemberLookupResult[];
}
export interface CrmAddMemberResult {
  id: number | null;
  name: string;
  subject_kind: string;
  subject_id: number;
  duplicate: boolean;
}

export interface CrmGridColumn {
  field_key: string;
  label: string;
  field_type: string; // crm_field_def.field_type (text/enum/multi_enum/number/currency/date/person(_multi)/organization(_multi)/...)
  comparator: string | null;
  // 'system'/'native'/'template'/'affinity_import' = a stored field_def (editable per type);
  // 'derived' = graph-computed, read-only (e.g. the person/org identity `name` column). Optional
  // for back-compat with older payloads that predate the field. See lists.list_columns (spec §9).
  origin?: string | null;
  // Slice 10: structured option groups (grouped + colored) for an enum-family column. When present,
  // the grid renders colored pills; absent/null → flat/legacy render. Colors are semantic tokens.
  groups?: { label: string; options: { value: string; color?: string }[] }[] | null;
  // PR3 item 7: where an ENTITY-REF column's eligible people come from. 'roster' = the active Ember
  // team (`lists.ROSTER_REF_OPTION_SOURCE`), which the backend already enforces on write (CUT-15(d)) —
  // the FE then renders a checklist of `CrmListOptionsResponse.ref_options[field_key]` instead of an
  // open directory typeahead. ⚠ Key the editor on THIS FLAG, never on a field key like 'deal_team':
  // eligibility is server-side data, and a new roster-only field must not need an FE release.
  ref_options_source?: string | null;
  // ── system-field catalog metadata (spec §9.2), present only on a catalog column ───────────────
  // ⚠ **Never branch on `origin` to decide editability.** Since W5 a system-field column carries
  // `origin: 'system'` (§9.2's flip) — and so does the STORED, EDITABLE `name` column on every
  // opportunity list, the IOT's deal name. Use these two capabilities through
  // `components/crm/crmSystemFields` (`isComputedColumn` / `isSystemColumn`), which is the one place
  // the frontend answers the question.
  //
  // THE editability capability: true = computed, never writable (§9.2: "frontend editability must
  // use read_only/capabilities, not an expanding list of magic field keys").
  read_only?: boolean;
  // 'record' | 'ai_review' | 'activity' | 'notes' — the §10.1 Customize View group, and the test for
  // "is this a system field at all".
  system_family?: string | null;
  // A stable explanation id ('last_contact_v1'), and the plain-language definition the cell tooltip
  // shows and Customize View searches over (§7.4 / §10.1).
  definition_key?: string | null;
  definition?: string | null;
  // §10.3 / §12.4: raw enum value → DISPLAY label, e.g. {'AI proposed': 'Proposed'}. Server-owned so
  // the vocabulary can change without a frontend release; the RAW value is untouched and stays what
  // filters store and sort compares. Absent ⇒ render the raw value.
  enum_labels?: Record<string, string> | null;
}

/** One entry of `CrmGridResponse.available_system_fields` (spec §9.3). */
export interface CrmSystemFieldChoice {
  field_key: string;
  label: string;
  field_type: string;
  family: string;
  /**
   * LIST-level enablement — distinct from per-view visibility (§10.2). The server's EFFECTIVE
   * state: since spec amendment A5 (2026-08-29) an eligible, resolvable system field reads `true`
   * with no list configuration at all, and `false` only where the list explicitly opted it out.
   */
  enabled: boolean;
  /** Why `enabled` is false: `true` = this list refused the field, absent/`false` = it can't serve it. */
  opted_out?: boolean;
  /** 'supported' | 'backfill_required' | 'unsupported' ('unsupported' is omitted from this list). */
  availability: string;
  definition_key: string;
  definition?: string | null;
  read_only: boolean;
  list_enablement: string;
  supports_sort: boolean;
  supports_filter: boolean;
  collides_with_native_field?: boolean;
}

export interface CrmGridSort {
  colId: string;
  sort: 'asc' | 'desc';
}

// Vendor-neutral getRows request (AG-Grid SSRM-shaped; a thin FE adapter maps AG-Grid's
// IServerSideGetRowsParams onto this). filter_model keys are field_key → AG-Grid filter spec;
// row_group_cols = ordered field_keys to group by; group_keys = the drilled group path.
export interface CrmGridRequest {
  start_row?: number;
  end_row?: number;
  sort_model?: CrmGridSort[];
  filter_model?: Record<string, unknown>;
  search?: string;
  row_group_cols?: string[];
  group_keys?: string[];
}

// A saved grid view (crm_view). config carries columnOrder/hiddenColumns/sortModel/filterModel/
// rowGroupCols/search + shared. Owned by a person; visible to owner + (if shared) everyone on the list.
export interface CrmView {
  id: number;
  list_id: number;
  name: string;
  view_type: string;
  config: Record<string, unknown>;
  owner_person_id: number | null;
  is_mine: boolean;
  shared: boolean;
  is_default: boolean; // MY OWN default view (auto-applied on open) — never another owner's
  // the LIST's team default, set by an admin: applied on open to anyone with no personal default of
  // their own. Ownerless, so everyone on the list sees the same flag; `is_default` always wins.
  is_team_default: boolean;
}

export interface CrmViewsResponse {
  views: CrmView[];
  /**
   * The CALLER's real admin capability, from the same resolution that gates the team-default route.
   *
   * ⚠ Gate the "set as team default" affordance on THIS, never on `auth/admins.ts`'s hardcoded
   * ADMIN_EMAILS mirror: `require_admin` also honors `team_roster.is_admin`, so a roster-flagged admin
   * passes the route while the mirror hides the button from them.
   */
  is_admin: boolean;
}

// A person/org reference cell value (crm_field_value value_json for entity-ref field types).
export interface CrmRefValue {
  affinity_id?: number;         // IOT import shape
  local_id?: number;            // L2 native-edited shape ({local_id, name})
  name: string | null;
  email?: string | null;
  local_person_id?: number | null;
  local_org_id?: number | null; // resolved local org id (for org-ref click-through)
  local_organization_id?: number | null; // what the IOT import writes for an org ref
  /** Role slugs on a fact-backed catch-all column (memo §15). Vocabulary slugs only — the governed
   *  gate admits them by shape, and `crmEntityRoles.formatRole` does the display casing. */
  roles?: string[];
  /**
   * §1.5(a) — which SIDE of the deal each role puts this entity on, so the catch-all columns can
   * subgroup by party UNDER the role ("Investment Banking → Buy side / Sell side").
   *
   * ⚠ Paired, not two parallel lists: `roles` is deduped, so a firm advising both sides in one
   * discipline collapses to a single role entry and the party would be lost. A role absent from here has
   * no party — render it FLAT, never under an "Unknown" heading, which would imply the question was
   * asked and unanswered when it was never askable. Both halves are vocabulary slugs; the server maps
   * `deal_party_role` → side from the register, never the front end.
   */
  role_parties?: { role: string; party: string }[];
  /**
   * Party-facts spec §5 rule 2 — the SUBSET of `roles` this chip renders only as implied, because
   * another chip on the same (entity, deal) entails it. A subset of `roles` by construction (the
   * governed gate intersects the two), so it can only ever fold a slug the cell already carries.
   *
   * Absent when `crm_party_display_fold_enabled` is off; `crmEntityRoles` falls back to its own
   * `unclassified` rule for a cell that predates the flag.
   */
  implied_roles?: string[];
  /** §15 item 2 — the person's current title. From the governed directory read, not a snapshot. */
  title?: string | null;
  /** §15 item 2 — the person's current employer. Present ONLY when that org itself passed the gate,
   *  so a restricted or merged-away org is absent rather than named. */
  employer?: { local_id: number; name: string | null; open_profile?: unknown } | null;
  /**
   * internal-profile-visibility-spec §3.3 — "you may see this person, but not open their profile".
   *
   * ⚠ NOT an authorization signal; `open_profile` is (see `EntityRefs.isAuthorized`). An internal
   * colleague on a deal team is fully visible and must keep rendering — only the LINK is suppressed,
   * because the profile page itself 404s for non-admins. Read it through `canOpenProfile`, never
   * directly. Absent → linkable, so pre-existing payloads render exactly as before.
   */
  profile_hidden?: boolean;
}

// L2 directory lookup (POST /crm/lookup) — the ref-cell typeahead.
export interface CrmLookupResult {
  kind: 'person' | 'organization' | 'opportunity';  // 'opportunity' (Item E) → a list_entry id
  id: number;
  name: string | null;
  detail?: string | null;  // email (person) / domain (org); absent for opportunities
  // opportunity hits only (search-quality spec Part 3): the parent list, for open + label
  list_id?: number;
  list_name?: string | null;
  /** NUX-1 mention search only: why the mixed-kind ranker placed this result. */
  reason?: 'exact' | 'prefix' | 'context' | 'text_match';
  contextual?: boolean;
}
export interface CrmLookupResponse {
  results: CrmLookupResult[];
}

// ── Manual organisation creation (identity notes §3) ──────────────────────────
// The CRM mints organisations only from ingested mail, so a company that has never emailed us cannot
// be represented at all. `POST /api/crm/admin/org-create` is SEARCH-FIRST: without `confirm_name` it
// returns the near-matches and writes nothing; with a matching `confirm_name` it creates.
/** One record that already looks like the company being typed in, and WHY it matched. */
export interface CrmOrgNearMatch {
  org_id: number;
  primary_name: string | null;
  primary_domain: string | null;
  domains: string[];
  /** `merged_away` / `soft_deleted` records appear on no other surface but still hold their domain. */
  state: 'live' | 'soft_deleted' | 'merged_away';
  merged_into_id: number | null;
  origin: string | null;
  manual_locked: boolean;
  /** Strongest first: a shared domain is identity; a shared name is only a resemblance. */
  basis: 'domain' | 'domain_alias' | 'name_exact' | 'name_contains';
  matched_on: string | null;
}
/** A domain the create would be REFUSED on — one domain belongs to one company. */
export interface CrmOrgDomainConflict {
  domain: string;
  org_id: number;
  primary_name: string | null;
  /**
   * WHERE the holder keeps it. Only `primary` is the slot the filtered unique index actually
   * refuses; `alt` and `alias` collisions are refused because two organisations claiming one domain
   * is the duplicate this flow exists to prevent, not because anything in the schema stops them —
   * so the operator needs the slot to know what to go and edit.
   */
  holder_slot: 'primary' | 'alt' | 'alias';
  state: string;
  merged_into_id: number | null;
}
export interface CrmOrgCreateResponse {
  created: boolean;
  /** Present on the search path (`created: false`). */
  name?: string;
  near_matches?: CrmOrgNearMatch[];
  domain_conflicts?: CrmOrgDomainConflict[];
  next_step?: string;
  /** Present on the create path (`created: true`). */
  org_id?: number;
  primary_name?: string;
  primary_domain: string | null;
  alt_domains?: string[];
  origin?: string;
  manual_locked?: boolean;
  declined_near_matches?: number[];
  /** Stated when the org has NO primary domain — what the operator gave up. Empty otherwise. */
  consequences?: string[];
  reindex_enqueued?: boolean;
}

// Per-cell overlay marker on an Affinity-backed row (M3.2): the operator's edit is rendered in
// place of the synced value. ⚰ `status: 'pending'` no longer means "queued for Affinity" — the
// Pillar-D funeral (2026-08-27) deleted the drain, so it means UNSENT, and the row is resolved by
// the next import's reconcile_overlay. push_error (M3.3) = a PRE-funeral failed write-through; it
// is write-dead and never retryable now, kept so a stored error stays visible.
export interface CrmOverlayMark {
  status: 'pending';
  set_by: string | null;
  set_at: string | null;
  push_error?: string | null;
}

export interface CrmGridResponse {
  // edit_mode: 'direct' = native list (edits write locally); 'overlay' = Affinity-backed (edits
  // land in the overlay, render as pending until Affinity syncs)
  list: {
    id: number; name: string; list_kind: string; edit_mode?: 'direct' | 'overlay';
    entry_subject_kind?: string | null; template_key?: string | null;  // L1: null = opportunity
    // list-sharing v1: may THIS caller manage the list's sharing (owner or sharee — and the list
    // has an owner; a NULL-owner system list is firm-wide by class, so there is nothing to share)?
    // `shared_count` = people who can ACTUALLY see it through a share: un-revoked rows whose holder
    // still holds the list's permission_class, deduped by person, owner rows excluded. 0 whenever
    // can_share is false.
    can_share?: boolean; shared_count?: number;
  };
  columns: CrmGridColumn[];
  // rows may carry _overlay: Record<field_key, CrmOverlayMark> on Affinity-backed lists
  rows: Array<Record<string, unknown>>;
  count: number;
  start_row: number;
  end_row: number;
  // non-null when `rows` are GROUP rows ({[group_field]: display, _group, _group_key, _child_count})
  group_field?: string | null;
  // M3.3 write-through status over the WHOLE list (not the page): pending = overlay edits not
  // yet in Affinity; failed = pending edits whose last push attempt errored (retry via sync)
  sync?: { pending: number; failed: number };
  // M3.4: count of active AI proposal marks across the list (rows carry _proposals maps) that THIS
  // caller may see. Proposal content is governed per row since the 2026-08-21 owner ruling — deal
  // team, evidence-attributed, or full_deal_access — so this is not a firm-wide total and two users
  // legitimately read different numbers on the same list. Server: lists._gate_proposal_marks.
  proposals?: number;
  // D8: how many rows in the (filtered) set are whole-record proposals (full-row green). A proposed
  // row carries `_proposed_record: { source?, source_id? }`; Accept promotes it, Dismiss suppresses it.
  proposed_count?: number;
  // the opt-in graph-derived interaction columns this list's subject kind supports — the Columns
  // panel offers the not-yet-active ones as "add"; enabling PATCHes /derived (config_json.derived).
  //
  // ⚠ KEPT as the §9.3 compatibility alias. It is subject-kind keyed, so it is EMPTY on the IOT and
  // can express neither the record nor the AI-review family — which is why W5's Customize View reads
  // `available_system_fields` below and this stays only for the legacy enable path on native lists.
  available_derived?: { field_key: string; label: string; field_type: string }[];
  // W1/§9.3: the server-owned catalog's view of the same question, plus the record and AI-review
  // families the alias above cannot express. `unsupported` entries are omitted (the admin diagnostic
  // route serves those). `enabled` is LIST-level state; per-view visibility is the grid's own
  // `hidden` set — §10.2 requires the two to stay visibly distinct operations.
  available_system_fields?: CrmSystemFieldChoice[];
}

// Item D (D2a/D3): one field's resolved state in the record panel — the shared ResolvedFieldState
// shape from the field-state kernel (record_field_states.v1). CRM statuses: unchanged | proposed
// (green) | edited (blue) | push_failed (red); accepted/dismissed reserved (CRM dismiss = suppression).
export interface CrmRecordFieldState {
  field_name: string;
  label?: string | null;
  field_type?: string;
  status: string;
  effective_value: unknown;
  proposed_value: unknown;
  write_value: unknown;
  write_eligible: boolean;
  pending: boolean;
  proposal_id?: number | null;
  push_error?: string | null;
  // notes-provenance: settled accepted-value provenance (crm_field_resolved_value).
  resolved?: boolean;
  resolved_at?: string | null;
  resolved_by_person_id?: number | null;
}

// Item D (D2c, spec R4): the per-list record-panel layout — sections of field_keys that group +
// order the panel's field grid. title=null renders no header (the default = one null-title section
// with every non-`name` field in column order, which renders exactly like the flat panel). Served by
// GET/PUT /crm/lists/{id}/record-layout and also bundled into CrmRecordResponse.layout.
export interface CrmRecordLayoutSection {
  title: string | null;
  field_keys: string[];
  /** 1 or 2 (memo §15 item 6). Server-coerced, so anything else never reaches here. */
  columns?: number;
}
export interface CrmRecordLayout {
  sections: CrmRecordLayoutSection[];
  /** Fields hidden from the panel. An EXCLUSION list, not a visibility whitelist — a field nobody
   *  hid is shown, which keeps a newly-added column from being invisible by default. A hidden field
   *  still renders when it carries a live proposal or a failed push (see `CrmRecordPanel.isHidden`). */
  hidden_field_keys?: string[];
}

// Item D (D2a/D3): the single-record read payload (GET /crm/lists/{id}/entries/{id}/record) — the
// resolved record row + record_field_states.v1, the record panel's authoritative source for actionable
// state (D2b hydrates from this before showing edit/accept/dismiss controls).
// Item D (D5, R6): the entry→deal bridge. Present (non-null) when this list_entry is a deal_pipeline
// row bound to an Affinity opportunity via crm_external_reference — the panel then embeds the
// Deal-Updates workflow (fed by /api/deals/{deal_key}). Null/absent → the panel renders fields only.
export interface CrmRecordDealRef {
  deal_kind: string;
  deal_key: string;
}

// Item D (D6): the entry→subject bridge. Present for a person/org list_entry so the panel's Timeline
// sub-view embeds the subject's governed CrmTimeline. Null/absent for opportunity entries (no subject).
export interface CrmRecordSubjectRef {
  kind: 'person' | 'organization';
  id: number;
}

// E2e-3 (§4.3.6): one CHIP in a PROJECTED column, and every relationship fact behind it.
//
// `facts` is a LIST because the cell dedups by entity while the ledger's active winner is unique per
// fact_key — so one person can hold two live facts on a deal (two roles, or two effective windows) and
// they render as one chip. The tri-action sends the whole list: deciding one of two retracts half the
// value and reports success. Each member carries the `revision` it is rendered at — the CAS token, so a
// decision made against a panel a producer has since moved is refused (409) rather than applied to a
// value nobody saw. Served per viewer: a chip whose target the viewer may not see is omitted entirely.
/**
 * ONE relationship fact behind a chip, with its OWN type and qualifiers.
 *
 * Spec §1.2: the modal lists every fact behind a chip separately, so an entity that is both a
 * participant and an adviser becomes two independently editable rows rather than one ambiguous chip
 * with no controls. `CrmProjectedChip.current_qualifiers` cannot serve that — it is the INTERSECTION
 * the members agree on, so a key they disagree about is absent from it entirely.
 *
 * ⚠ `qualifiers` never carries an ENTITY_REF value (`represented_party`, `context_ref`): those hold a
 * CRM entity id, which the two-gate contract may be withholding from this viewer, so the server drops
 * them. A Save sends only the keys it changed and the ledger merges, so an omitted qualifier survives.
 */
export interface CrmProjectedFact {
  assertion_id: number;
  revision: number;
  relationship_type: string;
  qualifiers: Record<string, string>;
  producer_kind: string;
  /**
   * WP-4 / spec lever C — the ORGANISATION this PERSON acted through on this fact, as a GOVERNED ref.
   *
   * Three-valued on purpose. The key is ABSENT when the concept does not apply (an organisation's own
   * fact, or a relationship type that does not carry a capacity); `null` when no firm is recorded —
   * which deliberately also covers a firm this viewer may not see, because saying "there IS one and
   * you may not know it" is the existence disclosure the entity gate exists to prevent.
   *
   * It does NOT ride in `qualifiers`: that map drops every entity ref, correctly, so the server
   * resolves this one through the same governed resolver the chip's own `ref` goes through.
   */
  acting_for?: { local_id: number; name: string | null; open_profile?: string | null } | null;
  /** THIS fact's answer. The chip-level flag is `all(...)`, which reads as undecided when one of two is. */
  human_decided: boolean;
}

export interface CrmProjectedChip {
  facts: CrmProjectedFact[];
  kind: 'person' | 'organization';
  /** Rebuilt by the governed resolver — current name + canonical id + a server-computed profile link. */
  ref: CrmRefValue & { local_id: number };
  producer_kinds: string[];
  /**
   * A human has already decided about EVERY fact behind this chip (manual author, or a prior Accept).
   *
   * ⚠ Chip-level, so it reads as UNDECIDED whenever one of two facts is. The modal decides about one
   * fact and uses `CrmProjectedFact.human_decided` instead.
   */
  human_decided: boolean;
  // ⚠ `relationship_types`, `role_options` and `current_qualifiers` were REMOVED with the inline chip
  // control (§1.1). They fed the row's single `<select>`; the modal reads the column's
  // `projected_add_options` (every admissible type with its ordered slots) plus each fact's own
  // `relationship_type` / `qualifiers`. `role_options` also cost one vocabulary query per qualifier
  // key per chip, which is why it is gone from the server rather than merely unused here.
}

/**
 * One typed input on a relationship type. The modal renders one control per slot, which is what makes
 * a two-qualifier fact (an adviser's discipline AND side) expressible at all.
 *
 * `key` is a vocabulary SLUG, never a display string — format it with `formatRole`. The server keeps
 * display text off the wire deliberately; see `deal_field_projection._role_of`.
 */
export interface CrmProjectedAddSlot {
  key: string;
  /** v1 renders `controlled` (a select) and `free_text` (an input). Spec §10 defers the other four. */
  control: 'controlled' | 'free_text';
  required: boolean;
  /** True ⇒ changing it later mints a NEW fact identity (a refute-and-recreate, not a revision). */
  identity_bearing: boolean;
  /** Non-null when the COLUMN pins this slot (adding to "Organizations" means adding a target). */
  fixed_value: string | null;
  /** ACTIVE vocabulary only — a retired value is never offered for a NEW fact. Empty for free text. */
  values: { value: string; label: string; description_terse: string | null }[];
}

export interface CrmProjectedAddType {
  relationship_type: string;
  /** The register's own label — authored, unlike a slug, so it is not formatted client-side. */
  label: string;
  slots: CrmProjectedAddSlot[];
}

/**
 * Spec §1.3 — what the read-only interpret endpoint returns. A DRAFT, never a write.
 *
 * The proposal fills the modal's slot controls, so every value stays editable before Accept: an operator
 * who disagrees overrides in place rather than re-typing. `questions` is the model asking (a question is
 * always better than a guess) PLUS anything the server dropped because it was not in the vocabulary.
 */
export interface CrmFactInterpretResponse {
  proposal: {
    relationship_type: string;
    qualifiers: Record<string, string>;
    from_kind: 'person' | 'organization';
    entity: {
      name: string;
      /** Resolved only on an unambiguous single match — never a ranked guess (§5). */
      local_id: number | null;
      candidates: { local_id: number; name: string | null }[];
    };
    /** Required slots the parse could not fill — the modal renders them blank to be finished by hand. */
    missing_required: string[];
  };
  questions: string[];
  already_recorded: boolean;
  turn: number;
  /** False once the second turn is spent: two turns maximum, stateless (§1.3). */
  can_ask_again: boolean;
  prompt_version: string | null;
  /** The operator's own words, for `decision_reason` on Accept so the audit reconstructs the chain. */
  operator_text: string;
}

/**
 * §3/P4 + spec §1.2: what an ADD on one projected column would create. Server-decided, per COLUMN —
 * an add has no chip to derive it from.
 *
 * ⚠ A column ABSENT from `projected_add_options` cannot express an add at all (no projection, or no
 * type that v1 can render) and must show no add control. Never synthesise one client-side: the write
 * would be refused.
 */
export interface CrmProjectedAddOptions {
  from_kind: 'person' | 'organization';
  /** Every type this column admits, each with its ordered slots. The modal's type-first cascade. */
  types: CrmProjectedAddType[];
}

export interface CrmRecordResponse {
  list: CrmGridResponse['list'];
  columns: CrmGridColumn[];
  record: Record<string, unknown>;
  field_states: CrmRecordFieldState[];
  // E2e-3: field_key → one entry per rendered chip. `{}` whenever the projection is dark.
  projected_facts?: Record<string, CrmProjectedChip[]>;
  /** §3/P4: per-column add inputs. A column absent here shows no add control — see the interface. */
  projected_add_options?: Record<string, CrmProjectedAddOptions>;
  // R4-1: record_review_v2 tells the panel whether to hydrate the governed v2 field-review (proposed
  // AND committed rows) vs the v1 path. Optional for back-compat with older payloads (absent → false).
  // E1a: `record_timeline` mirrors crm_record_timeline_enabled so the panel offers the consolidated
  // timeline sub-view without probing for it. Absent → false → no sub-tab and no lane fetch.
  capabilities: {
    editable: boolean; proposals: boolean; record_review_v2?: boolean; record_timeline?: boolean;
  };
  // D2c: the resolved record layout (sections). Optional for back-compat with older payloads; when
  // absent the panel falls back to its flat all-columns render.
  layout?: CrmRecordLayout;
  // D5 (R6): the entry→deal bridge, or null when the entry isn't a deal_pipeline row.
  deal_ref?: CrmRecordDealRef | null;
  // D6: the entry→subject bridge, or null when the entry has no person/org subject (opportunity).
  subject_ref?: CrmRecordSubjectRef | null;
  // notes-provenance §2.1a/§9: the Recent-Updates blocks (Progress / New Info) — a pending block
  // proposal to review + the settled/accepted bullets. Rendered natively in the record panel.
  recent_updates?: CrmRecentUpdates;
  schema_version: string;
}

/** One Recent-Updates block (Progress or New Info). `proposed` = the pending block proposal's bullets
 * (awaiting review); `resolved` = the settled/accepted bullets already captured (each accept also
 * materialized a CRM note). */
/** One bullet inside a block. `group` (S3 grouped summaries, bounded-emission design §4.3) is a
 * FREE-TEXT header the model assigns — the six suggested groups are examples, never an enum, so
 * neither this type nor the server validates its value. Optional and additive: an item without one
 * renders as an ungrouped bullet, which is every item stored before S3. */
export interface CrmBlockItem {
  text: string;
  /** S5a-2 stable identity: a content hash of the normalized `text` (never model-supplied). */
  item_id?: string;
  evidence_event_ids?: number[];
  /** S2 §4.3 sub-header. FREE TEXT — never an enum (owner ruling 2026-08-09). */
  group?: string | null;
  /** S2 ranking key for the per-section cap. Carried by the writer; NOT rendered today. */
  materiality?: string | null;
  /** Item ids this bullet retires (S5a-2). */
  supersedes?: string[];
  /** S2 synthetic-rollup markers. */
  rollup?: boolean;
  overflow_count?: number;
}

export interface CrmBlockUpdate {
  proposal_id: number | null;
  proposed: CrmBlockItem[] | null;
  resolved: CrmBlockItem[] | null;
  review_deadline_at?: string | null;
}

export interface CrmRecentUpdates {
  progress?: CrmBlockUpdate;
  new_info?: CrmBlockUpdate;
}

// ── record_review.v2 (R1) — the governed, versioned field-review read model ────────────────────
// A FRESH governed per-field payload (backend crm/record_review.py), served at
// GET /crm/lists/{id}/entries/{id}/record/v2 behind settings.crm_record_review_v2_enabled (DARK by
// default → 404 until flipped). Deliberately NARROWER than CrmRecordResponse: it carries only the
// authoritative field-review core (per-field disposition + allowed_actions + values + the CAS token),
// NOT the panel scaffold (record/columns/layout/deal_ref/subject_ref/recent_updates — those keep
// coming from useCrmRecord v1). R1d ships this as strict types + a read-only shadow adapter
// (adaptRecordReviewV2) with v1 authoritative; R5-FE/R4 make the panel consume it. The
// `proposal_set_hash` + `record_revision` pair is the R2b record-level CAS token the panel echoes back
// on Accept/Pass/Dismiss (a newer proposal-set / revision → 409 stale).

/** Push/external-sync status for one v2 field. `failed` carries the last push error; CRM-local settled
 * values are `none` (R3 delivery is dark — CRM accepts don't reach Affinity yet). */
export interface RecordReviewV2ExternalSync {
  status: 'none' | 'failed';
  error: string | null;
}

/** AI-proposal annotation (confidence / materiality / reason) — present only on member fields. */
export interface RecordReviewV2Annotation {
  confidence: number | string | null;
  materiality: number | string | null;
  reason: string | null;
}

/** One field in a record_review.v2 payload. `disposition` is the governed review state; `status`
 * mirrors it plus `unchanged` for non-member fields. `allowed_actions` is server-computed by
 * lifecycle × disposition × editability — a PROPOSED (draft) row omits field-level `accept`
 * (record-level Accept only), offering edit/reject/restore; a committed row offers the full set.
 * `is_member` = the field is part of the proposal-set that drives the CAS hash. */
export interface RecordReviewV2Field {
  field_name: string;
  label: string;
  field_type: string;
  status: string;
  is_member: boolean;
  disposition: string | null;
  current_value: unknown;
  proposed_value: unknown;
  effective_value: unknown;
  change_kind: 'add' | 'replace' | 'remove' | null;
  proposal_id: number | null;
  annotation: RecordReviewV2Annotation | null;
  reject_reason: string | null;
  restored_from_proposal_id: number | null;
  review_deadline_at: string | null;
  allowed_actions: string[];
  external_sync: RecordReviewV2ExternalSync;
}

/** GET /crm/lists/{id}/entries/{id}/record/v2 (DARK behind crm_record_review_v2_enabled). The governed
 * field-review core + the record-level CAS token + lifecycle-resolved record actions. */
export interface CrmRecordReviewV2Response {
  schema_version: string;              // "record_review.v2"
  record_revision: number;
  record_lifecycle: string;            // committed | proposed | ...
  proposal_set_members: string[];      // normalized member tokens
  included_members: string[];          // members whose disposition counts toward "included"
  proposal_set_hash: string;           // CAS token — echoed on record-level commands (R2b)
  record_actions: string[];            // proposed: accept|pass|dismiss|merge · committed: accept | []
  // S3 (2026-07-24): the count of the caller's actionable, still-`pending` committed members — the
  // exact set a record-level "Accept all" resolves. Gates `record_actions` including 'accept' for a
  // committed row (NOT the includable {pending,edited} set, which also counts an already-edited field
  // accept-all can't touch — the pre-fix dead "Accept all (0)" cause). Meaningful for committed rows;
  // present but not consulted by the FE for proposed rows (their record_actions are the fixed set).
  pending_count: number;
  capabilities: { editable: boolean; proposals: boolean };
  fields: RecordReviewV2Field[];
}

/** The R2b record-level CAS token — the {record_revision, proposal_set_hash} pair a client echoes on a
 * review / record command so the server rejects (409) a write against a stale proposal-set. Sourced
 * from a rendered record_review.v2 payload (adaptRecordReviewV2(...).reviewCas). Snake_case matches the
 * backend `_ReviewCasBody` exactly so it spreads straight into a request body. Omit → no record-level
 * staleness check (per-proposal CAS only). */
export interface RecordReviewCas {
  record_revision: number;
  proposal_set_hash: string;
}

// Raw evidence timeline for a deal record (proposed candidate OR committed opp) — the emails/meetings
// the classifier bound to it (deal_event_log), D8-gated + per-mailbox §9-gated server-side. Reuses the
// shared EvidenceItem shape. hidden_count = unique emails not from the viewer's mailbox (folded away).
/** One `deal_portco_link` row, as the record panel reads it (portco-transactions spec §2.2). */
export interface CrmPortcoLink {
  id: number;
  portfolio_company_id: number;
  portfolio_company_name: string;
  deal_kind: string;
  deal_key: string;
  /** 'platform' | 'add_on' | 'exit' */
  link_kind: string;
  /** 'seed' | 'operator' | 'phase_b_accepted' — who decided, and it is load-bearing: Phase B only
   *  ever PROPOSES (spec invariant 3), so a machine value here would be a bug, not a state. */
  provenance: string;
  confidence: string | null;
  evidence_note: string | null;
}

/** WP1 / S1.2 — the record panel's parent-portco section.
 *
 * ⚠ `links` is an ARRAY. A merge of two linked deals fronts two parents (invariant 1 calls that a
 * data error to SHOW), which is what `conflict` reports. Never render `links[0]` alone. */
export interface CrmPortcoLinkResponse {
  entry_id: number;
  deal_ref: { deal_kind: string; deal_key: string };
  links: CrmPortcoLink[];
  conflict: boolean;
  /** Whether THIS caller may change the parent — deal team, or `full_deal_access`. */
  editable: boolean;
  link_kinds: string[];
  /** Picker options; empty for a read-only caller. */
  candidates: { id: number; name: string }[];
}

/** WP-7 / lever F — one chip on the timeline's party row. `id` is the tree's own node id
 * (`org:12` / `person:34`), so the chip filters on the id it was given rather than assembling one.
 * `count` is every message on this deal this viewer can see with this party — the same number PR-F's
 * popover footer prints as "all N messages with this party". */
export interface CrmTimelineParty {
  id: string;
  kind: 'organization' | 'person';
  local_id: number;
  name: string | null;
  count: number;
}

/** WP-7 — present ONLY when `crm_timeline_party_filter_enabled` is on; its absence is what the FE
 * reads as "the feature is dark", which is a different statement from "this deal has no parties".
 *
 * `applied` is the subset of the requested ids that SURVIVED membership + the entity gate. It can be
 * shorter than what the client sent, and when it is empty after a non-empty request the server has
 * returned zero rows on purpose (fail closed) — the client must say so rather than reading the empty
 * stream as "this deal is quiet". */
export interface CrmTimelinePartyFilter {
  parties: CrmTimelineParty[];
  applied: string[];
  filtered: boolean;
  types: string[] | null;
  before: string | null;
  after: string | null;
}

export interface CrmRecordEvidenceResponse {
  evidence: EvidenceItem[];
  hidden_count: number;
  deal_kind: string | null;
  deal_key: string | null;
  party_filter?: CrmTimelinePartyFilter;
}

// ── Party-facts WP-6b / lever E2 — the governed fact-evidence trace behind the "why?" popover ──
//
// ⛔ EVERY NAME HERE CAME THROUGH THE ENTITY GATE, and there is deliberately nowhere to put an
// address: `from` is a ref or null, recipients are refs plus a COUNT of the ones withheld. A
// participant the gate declined is `to_hidden`, never a string.

/** One party on a cited email, as the governed directory names them. */
export interface CrmFactEvidenceRef {
  local_id: number;
  name: string | null;
  open_profile?: { kind: string; id: number } | null;
}

export interface CrmFactCitation {
  /** The row the record timeline is showing — what the popover scrolls that timeline to. */
  event_log_id: number;
  occurred_at: string | null;
  subject: string | null;
  from: CrmFactEvidenceRef | null;
  /** True when the sender exists but this viewer may not be told who — distinct from "no sender". */
  from_hidden: boolean;
  to: CrmFactEvidenceRef[];
  /** Recipients this viewer may not be told about. A bcc is neither listed NOR counted here. */
  to_hidden: number;
  /** `stated` | `corroborating` | `presence` — WP-6a's closed vocabulary, ranked in that order. */
  relevance: string | null;
  ordinal: number | null;
  /** X-4: the party's envelope position ON THIS EMAIL (`from`/`to`/`cc`), not on the fact. */
  appearance: string | null;
  /** X-4: `inbound` / `outbound`, from the FIRM's perspective. */
  direction: string | null;
  /** Lever I rule 5 — the template-composed citation note. Rendered only inside the popover. */
  note: string | null;
  body_available: boolean;
}

export interface CrmFactChainLink {
  /** `null` on a `retired_by` that is a REASON rather than a successor (a refutation). */
  assertion_id: number | null;
  revision?: number;
  decision_state?: string;
  reason?: string | null;
}

export interface CrmFactNote {
  id: number;
  author_kind: 'human' | 'machine';
  author_person_id: number | null;
  producer_kind: string | null;
  text: string;
  created_at: string | null;
  assertion_id_at_write: number;
  /** HUMAN rows only, and COMPUTED at read time: the role or the evidence moved after they wrote. */
  stale?: boolean;
}

export interface CrmFactEvidenceResponse {
  assertion_id: number;
  relationship_type: string;
  /**
   * ⛔ There is deliberately NO `fact_key` here (adversarial review 2026-09-05, F1 · P1). It is a
   * sha256 over the fact's IDENTITY-BEARING qualifiers — `acting_for`, `represented_party` — and
   * every other input to that hash rides in this payload, so publishing it hands a caller a
   * brute-forceable commitment to exactly the entity ids the gate withheld. Nothing rendered it.
   */
  revision: number;
  /** Where the cited rows live, so the popover can open the timeline without being told. */
  list_id: number;
  entry_id: number;
  producer_kind: string;
  observed_at: string | null;
  acting_for: CrmFactEvidenceRef | null;
  citations: CrmFactCitation[];
  chain: {
    supersedes: CrmFactChainLink | null;
    retired_by: CrmFactChainLink | null;
    active: boolean;
    decision_state: string;
    decided_via: string | null;
    decided_by: CrmFactEvidenceRef | null;
    decided_at: string | null;
  };
  notes: { human: CrmFactNote | null; machine: CrmFactNote | null };
  /** The footer: lever F's node id and ITS OWN count. `count` is null while the filter is dark. */
  party: { node_id: string; kind: string; local_id: number; name: string | null;
           count: number | null };
}

export interface CrmFactNoteWriteResponse {
  status: string;
  note_id: number | null;
  author_kind: string;
  lineage_key: string;
}

// Pillar F.5 — the per-item body door's payload, on both the CRM record panel
// (`/api/crm/lists/{id}/entries/{id}/evidence/{event_log_id}/body`, audited) and the Deals tab
// (`/api/deal-cards/{key}/evidence/{event_log_id}/body`). One row's text, on click; 404 otherwise.
export interface CrmEvidenceBodyResponse {
  body: string;
  event_log_id: number;
}

// What a record-level Accept / Pass MATERIALIZED into the settled layer. `refused` is not cosmetic: a
// fresh-authored draft the write gate refuses at publish time (e.g. a deal-team member who left the
// roster between the draft edit and the Accept) is reported HERE and nowhere else — the whole-row overlay
// clear then removes it, so a caller that ignores this field silently loses the operator's typed value.
export interface CrmMaterializedFields {
  accepted: number;
  authored: number;
  refused: { field_key: string | null; reason: string }[];
}

export interface CrmAcceptRecordResponse {
  status: string;
  opp_id: number | null;
  materialized?: CrmMaterializedFields;
}

// ⚑ refute of one evidence row on a CRM record (POST …/evidence/{event_log_id}/refute). Mirrors the
// Deals tab's unbind result: `swept_siblings` = the OTHER mailbox copies of the same email this call
// also flipped to noise; `auto_dismissed` = candidates that lost their last non-noise event.
export interface CrmRecordEvidenceRefuteResponse {
  status: string;
  event_log_id: number;
  deal_kind: string | null;
  deal_key: string;
  audit_id: number | null;
  already_noise: boolean;
  swept_siblings: number;
  auto_dismissed: { deal_key: string; dismiss_event_id: number }[];
  // Queue-only Phase B (owner ruling 2026-08-20) — `status` is "queued" now;
  // `job_id` is the enqueued row (dedupe means several deals can share one).
  aggregated: { deal_kind: string; deal_key: string; status?: string; job_id?: number }[];
}

// M3.4: an active AI proposal on one cell — rendered as a green mark; the cell CONTENT stays
// the resolved truth. rows carry _proposals: Record<field_key, CrmProposalMark>.
// GOVERNED (owner ruling 2026-08-21): a row carries `_proposals` only for a caller on its deal team,
// in the proposal's evidence audience, or holding full_deal_access — absence means "not yours to
// see", NOT "no pending proposal". Never infer a firm-wide pending state from a missing map.
export interface CrmProposalMark {
  value: unknown;
  proposal_id: number;
  set_at: string | null;
}

// PR3 item 7: one eligible person for a roster-only ref column's checklist picker. Id + name ONLY —
// the backend never sends an address on a governed entity link (`entity_links.scrub_ref_cell`), so the
// picker has no address to search on or display. Committed as a `CrmRefValue` ({local_id, name}).
export interface CrmRosterOption {
  local_person_id: number;
  name: string;
}

// Plan phase 1b: `stage_mapping` ladder membership, so the Stage dropdown can be contingent on the
// record's deal_type. DATA ONLY — `options` above is still every option the field stores, and the
// filtering happens client-side in `crm/stageLadder.ts`. Null/absent ⇒ no contingency on this list.
export interface CrmStageLadders {
  /** the list's Stage field. Server-owned, so no frontend release names it. */
  field_key: string;
  /** the field whose value picks the ladder. */
  deal_type_field_key: string;
  /** the (already lowercased) deal_type value that selects the exit ladder. */
  exit_deal_type: string;
  /** the `stage_mapping.stage_set` value naming the exit ladder. */
  exit_set: string;
  /** the set an unmapped stage value falls back to — `stage_mapping.stage_set`'s server default. */
  default_set: string;
  /** stage value → `stage_mapping.stage_set`. */
  sets: Record<string, string>;
}

// M3.3: {field_key → display labels} for a list's enum-ish columns (select editors)
export interface CrmListOptionsResponse {
  options: Record<string, string[]>;
  // PR3 item 7: {field_key → eligible people} for the columns flagged `ref_options_source: 'roster'`.
  // Always present ({} when the list has none); optional here for back-compat with cached payloads.
  ref_options?: Record<string, CrmRosterOption[]>;
  // plan 1b; null on a list with no Stage field or no deal_type field to be contingent on.
  stage_ladders?: CrmStageLadders | null;
}

// ── Item E (E1): CRM Notes ──────────────────────────────────────────────────────────────────────
// Note link/mention target. 'opportunity' is a suggester/adder alias the server maps to a
// target_kind='list_entry' row (there is no 'opportunity' target_kind).
export type CrmNoteTargetKind = 'person' | 'organization' | 'ember_entity' | 'list_entry';
export type CrmNoteVisibility = 'firm' | 'author_only' | 'team';

export interface CrmNoteLink {
  link_id: number;
  target_kind: CrmNoteTargetKind;
  target_id: number;
  source: 'manual' | 'mention';
  name: string | null; // resolved via the governed directory read; null = withheld/unresolvable
}

export interface CrmNoteAuthor {
  id: number;
  name: string | null;
}

// Full note (GET /crm/notes/{id}) — the overlay's authoritative source.
export interface CrmNote {
  id: number;
  body_json: unknown; // TipTap doc (tiptap.v3)
  body_format: string;
  revision: number; // N5 optimistic lock — echoed as expected_revision on PATCH
  visibility_mode: CrmNoteVisibility;
  permission_class: string; // derived (never client-set)
  audience_restricted: boolean;
  author: CrmNoteAuthor | null;
  created_at: string | null;
  updated_at: string | null;
  can_edit: boolean;
  can_delete: boolean;
  links: CrmNoteLink[];
}

// One row in an entity's note list (GET /crm/notes?target_kind=&target_id=).
export interface CrmNoteListItem {
  id: number;
  author: CrmNoteAuthor | null;
  created_at: string | null;
  updated_at: string | null;
  preview_text: string | null;
  visibility_mode: CrmNoteVisibility;
  link_count: number;
}

export interface CrmNoteListResponse {
  items: CrmNoteListItem[];
  has_more: boolean;
}

// A soft-delete result (PATCH that emptied the body, or DELETE) — the note is gone.
export interface CrmNoteDeleted {
  id: number;
  deleted: true;
}
